import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  Fragment,
} from 'react';
import { createPortal } from 'react-dom';
import type { RefObject, ReactNode, CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Plus,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Loader,
  X,
  BarChart3,
  Tag,
  Settings,
  CalendarDays,
  Edit3,
  Activity,
  Archive,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Trash2,
  User,
  Clock,
  AlertTriangle,
  Layers,
  Pencil,
  GripVertical,
  LayoutGrid,
  Rows3,
  PartyPopper,
  ExternalLink,
} from 'lucide-react';

import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  coerceKanbanBoardSettings,
  KanbanApiError,
  emptyKanbanBoardPlaceholder,
  kanbanApiService,
  cardFullToBoardCardSummary,
} from '../services/kanbanApi';
import { PlayerProfile } from '../components/PlayerProfile';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { eventTeamApiService } from '../services/eventTeamApi';
import { getEventTeamPerms } from '../utils/eventTeamPermissions';
import { EventStatusBadge } from '../components/eventteam/EventStatusBadge';
import type { EventItem } from '../types/eventTeam';
import { KanbanCardMini } from '../components/kanban/KanbanCardMini';
import { KanbanStackedCards } from '../components/kanban/KanbanStackedCards';
import { KanbanCardTypeBadge } from '../components/kanban/KanbanCardTypeBadge';
import { KanbanListRoleBadge } from '../components/kanban/KanbanListRoleBadge';
import { KanbanWIPIndicator } from '../components/kanban/KanbanWIPIndicator';
import { KanbanPriorityBadge } from '../components/kanban/KanbanPriorityBadge';
import { KanbanClassOfServiceIndicator } from '../components/kanban/KanbanClassOfServiceIndicator';
import { EditBoardModal } from '../components/kanban/EditBoardModal';
import { BoardAccessModal } from '../components/kanban/BoardAccessModal';
import { CreateCardModal } from '../components/kanban/CreateCardModal';
import { CreateListModal } from '../components/kanban/CreateListModal';
import { EditListModal } from '../components/kanban/EditListModal';
import { CreateSwimlaneModal } from '../components/kanban/CreateSwimlaneModal';
import { KanbanLabelManagementModal } from '../components/kanban/KanbanLabelManagementModal';
import { KanbanCadenceModal } from '../components/kanban/KanbanCadenceModal';
import { KanbanBoardSettingsModal } from '../components/kanban/KanbanBoardSettingsModal';
import { KanbanMetricsPanel } from '../components/kanban/KanbanMetricsPanel';
import { AddCardLinkModal } from '../components/kanban/AddCardLinkModal';
import { KanbanCardDetailPanel } from '../components/kanban/KanbanCardDetailPanel';
import {
  type KanbanFullBoard,
  type KanbanCardFull,
  type KanbanCardSummary,
  type KanbanActivityLogEntry,
  type KanbanCadence,
  type KanbanStats,
  type KanbanSwimlane,
  type KanbanList,
  KANBAN_CARD_TYPES,
  KANBAN_CLASSES_OF_SERVICE,
  CADENCE_TYPE_CONFIG,
  type KanbanCardType,
  type KanbanClassOfService,
  type KanbanCard,
  type KanbanCardLink,
} from '../types/kanban';
import { cn } from '../utils/cn';
import { getKanbanBoardPermissions } from '../utils/kanbanPermissions';
import { usernameCacheService } from '../services/usernameCache';
import { LayoutGroup, motion } from 'framer-motion';
import { useKanbanBoardWebSocket } from '../hooks/useKanbanRealtime';
import { mergeKanbanBoardEvent } from '../utils/mergeKanbanRealtime';
import type { KanbanWsEnvelope } from '../types/kanbanRealtime';
import {
  buildBoardCardStackRows,
  buildDescendantIdsByCardId,
  findSubtaskToReassignBelowInsert,
  flattenStackRowsToOrderedCardIds,
  intraStackDropSlot,
  stackRowCardIds,
  stackRowForDraggedCardId,
  stackRowsWithGhostAtFlatIndex,
  stackRowsWithoutMovedCards,
} from '../utils/kanbanCardStacks';

/** Coerce optional API fields so sync logic never throws on missing arrays. */
function normalizeFullBoardArrays(
  board: KanbanFullBoard | null | undefined,
  boardIdForPlaceholder?: string,
): KanbanFullBoard {
  if (!board || typeof board !== 'object') {
    return {
      board: emptyKanbanBoardPlaceholder(boardIdForPlaceholder ?? ''),
      cards: [],
      lists: [],
      swimlanes: [],
      labels: [],
      settings: [],
    };
  }
  const cards = (Array.isArray(board.cards) ? board.cards : []).map((c) => ({
    ...c,
    links: Array.isArray(c.links) ? c.links : undefined,
    labels: Array.isArray(c.labels) ? c.labels : [],
    assignees: Array.isArray(c.assignees) ? c.assignees : [],
    checklist_progress:
      c.checklist_progress &&
      typeof c.checklist_progress === 'object' &&
      typeof c.checklist_progress.total === 'number' &&
      typeof c.checklist_progress.checked === 'number'
        ? c.checklist_progress
        : { total: 0, checked: 0 },
    comment_count: typeof c.comment_count === 'number' ? c.comment_count : 0,
    attachment_count: typeof c.attachment_count === 'number' ? c.attachment_count : 0,
    watcher_count: typeof c.watcher_count === 'number' ? c.watcher_count : 0,
    link_count: typeof c.link_count === 'number' ? c.link_count : 0,
  }));
  return {
    ...board,
    cards,
    lists: Array.isArray(board.lists) ? board.lists : [],
    swimlanes: Array.isArray(board.swimlanes) ? board.swimlanes : [],
    labels: Array.isArray(board.labels) ? board.labels : [],
    settings: Array.isArray(board.settings) ? board.settings : [],
  };
}

function safeBuildBoardSyncSummary(prev: KanbanFullBoard, next: KanbanFullBoard): string | null {
  try {
    return buildBoardSyncSummary(prev, next);
  } catch (e) {
    console.warn('Board sync summary skipped:', e);
    return null;
  }
}

/** Short human-readable summary when board payload changes (for toast after silent refresh). */
function buildBoardSyncSummary(prevBoard: KanbanFullBoard, nextBoard: KanbanFullBoard): string | null {
  const prev = normalizeFullBoardArrays(prevBoard);
  const next = normalizeFullBoardArrays(nextBoard);
  const prevCardIds = new Set(prev.cards.map((c) => c.id));
  const nextCardIds = new Set(next.cards.map((c) => c.id));
  const cardsAdded = [...nextCardIds].filter((id) => !prevCardIds.has(id)).length;
  const cardsRemoved = [...prevCardIds].filter((id) => !nextCardIds.has(id)).length;

  const prevListIds = new Set(prev.lists.map((l) => l.id));
  const nextListIds = new Set(next.lists.map((l) => l.id));
  const listsAdded = [...nextListIds].filter((id) => !prevListIds.has(id)).length;
  const listsRemoved = [...prevListIds].filter((id) => !nextListIds.has(id)).length;

  const prevSw = new Set(prev.swimlanes.map((s) => s.id));
  const nextSw = new Set(next.swimlanes.map((s) => s.id));
  const swimAdded = [...nextSw].filter((id) => !prevSw.has(id)).length;
  const swimRemoved = [...prevSw].filter((id) => !nextSw.has(id)).length;

  const parts: string[] = [];
  if (cardsAdded) parts.push(`${cardsAdded} card${cardsAdded === 1 ? '' : 's'} added`);
  if (cardsRemoved) parts.push(`${cardsRemoved} card${cardsRemoved === 1 ? '' : 's'} removed`);
  if (listsAdded) parts.push(`${listsAdded} list${listsAdded === 1 ? '' : 's'} added`);
  if (listsRemoved) parts.push(`${listsRemoved} list${listsRemoved === 1 ? '' : 's'} removed`);
  if (swimAdded) parts.push(`${swimAdded} swimlane${swimAdded === 1 ? '' : 's'} added`);
  if (swimRemoved) parts.push(`${swimRemoved} swimlane${swimRemoved === 1 ? '' : 's'} removed`);

  if (parts.length > 0) return parts.join(' · ');

  let cardChanged = false;
  for (const c of prev.cards) {
    const n = next.cards.find((x) => x.id === c.id);
    if (!n) continue;
    if (
      n.list_id !== c.list_id ||
      n.swimlane_id !== c.swimlane_id ||
      n.position !== c.position ||
      n.title !== c.title ||
      n.is_blocked !== c.is_blocked
    ) {
      cardChanged = true;
      break;
    }
  }
  if (cardChanged) return 'Cards updated';

  const listMeta = prev.lists.some((l) => {
    const n = next.lists.find((x) => x.id === l.id);
    return n && (n.title !== l.title || n.wip_limit !== l.wip_limit);
  });
  if (listMeta) return 'Lists updated';

  const swMeta = prev.swimlanes.some((s) => {
    const n = next.swimlanes.find((x) => x.id === s.id);
    return n && (n.title !== s.title || n.is_collapsed !== s.is_collapsed);
  });
  if (swMeta) return 'Swimlanes updated';

  const labelMeta =
    prev.labels.length !== next.labels.length ||
    prev.labels.some((lb) => {
      const n = next.labels.find((x) => x.id === lb.id);
      return !n || n.name !== lb.name || n.color !== lb.color;
    });
  if (labelMeta) return 'Labels updated';

  if (
    prev.board.title !== next.board.title ||
    prev.board.description !== next.board.description ||
    prev.board.updated_at !== next.board.updated_at
  ) {
    return 'Board updated';
  }

  const settingsChanged =
    prev.settings.length !== next.settings.length ||
    JSON.stringify(prev.settings) !== JSON.stringify(next.settings);
  if (settingsChanged) return 'Board settings updated';

  return null;
}

const KANBAN_CARD_SEL = '[data-kanban-card]';
const KANBAN_STACK_ROOT_SEL = '[data-kanban-stack-root]';

const BOARD_LAYOUT_STORAGE_KEY = 'kanban:boardLayout:v1';

type BoardColumnLayout = 'columns' | 'swimlanes';

function readStoredBoardLayout(): BoardColumnLayout {
  try {
    const raw = localStorage.getItem(BOARD_LAYOUT_STORAGE_KEY);
    if (raw === 'swimlanes' || raw === 'columns') return raw;
  } catch {
    /* ignore */
  }
  return 'columns';
}

function columnRefKey(listId: string, swimlaneId: string | null | undefined, boardUsesSwimlaneBuckets: boolean) {
  if (!boardUsesSwimlaneBuckets) return `${listId}:__flat__`;
  return `${listId}:${swimlaneId ?? '__none__'}`;
}

/** Insert index in flat card order with the whole dragged bundle omitted (0 = before first card). */
function listInsertIndexFromPointer(
  clientY: number,
  columnRoot: HTMLElement,
  flatOrderedCardIds: string[],
  draggedBundleIds: Set<string>,
): number {
  const without = flatOrderedCardIds.filter((id) => !draggedBundleIds.has(id));
  let insertAt = without.length;
  for (let i = 0; i < without.length; i++) {
    const id = without[i];
    const stackRoot = columnRoot.querySelector(`[data-kanban-stack-root="${id}"]`) as HTMLElement | null;
    const el =
      stackRoot ??
      (columnRoot.querySelector(`${KANBAN_CARD_SEL}[data-kanban-card="${id}"]`) as HTMLElement | null);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    const mid = r.top + r.height / 2;
    if (clientY < mid) {
      insertAt = i;
      break;
    }
  }
  return insertAt;
}

function positionForInsert(
  flatOrderedCardIds: string[],
  draggedBundleIds: Set<string>,
  insertAt: number,
  positionById: Map<string, number>,
): number {
  const without = flatOrderedCardIds.filter((id) => !draggedBundleIds.has(id));
  const prevId = insertAt > 0 ? without[insertAt - 1] : undefined;
  const nextId = insertAt < without.length ? without[insertAt] : undefined;
  const prevP = prevId !== undefined ? positionById.get(prevId) : undefined;
  const nextP = nextId !== undefined ? positionById.get(nextId) : undefined;
  if (prevP === undefined && nextP === undefined) return 0;
  if (prevP === undefined) return (nextP ?? 1) - 1;
  if (nextP === undefined) return prevP + 1;
  return (prevP + nextP) / 2;
}

/** For solo subtask reparent: pick the adjacent non-subtask (prefer card above the gap). */
function resolveReparentNonSubtaskParent(
  flatWithoutBundle: string[],
  insertAt: number,
  cardById: Map<string, Pick<KanbanCardSummary, 'id' | 'card_type'>>,
): string | null {
  const prevId = insertAt > 0 ? flatWithoutBundle[insertAt - 1] : undefined;
  const nextId = insertAt < flatWithoutBundle.length ? flatWithoutBundle[insertAt] : undefined;
  const prev = prevId ? cardById.get(prevId) : undefined;
  const next = nextId ? cardById.get(nextId) : undefined;
  if (prev && prev.card_type !== 'subtask') return prev.id;
  if (next && next.card_type !== 'subtask') return next.id;
  return null;
}

/** Authoritative fields from PATCH /move JSON (HTTP is source of truth per WS spec). */
function applyMoveCardApiResponse(
  prev: KanbanFullBoard,
  cardId: string,
  api: KanbanCard,
): KanbanFullBoard {
  const cards = prev.cards.map((c) => {
    if (c.id !== cardId) return c;
    return {
      ...c,
      list_id: api.list_id,
      board_id: api.board_id,
      swimlane_id: api.swimlane_id,
      title: api.title,
      description: api.description,
      position: api.position,
      priority: api.priority,
      card_type: api.card_type,
      class_of_service: api.class_of_service,
      due_date: api.due_date,
      start_date: api.start_date,
      completed_at: api.completed_at,
      is_blocked: api.is_blocked,
      blocked_reason: api.blocked_reason,
      is_archived: api.is_archived,
      cover_color: api.cover_color,
      cover_image_url: api.cover_image_url,
      list_entered_at: api.list_entered_at,
      metadata: api.metadata,
      created_at: api.created_at,
      created_by: api.created_by,
      updated_at: api.updated_at,
      updated_by: api.updated_by,
      // Move response omits summary-only fields; keep prior board row data so chips stay correct.
      labels: c.labels,
      assignees: c.assignees,
      checklist_progress: c.checklist_progress,
      comment_count: c.comment_count,
      attachment_count: c.attachment_count,
      watcher_count: c.watcher_count,
      link_count: c.link_count,
    };
  });
  return { ...prev, cards };
}

function applyMoveCardApiResponseMany(
  prev: KanbanFullBoard,
  updates: { cardId: string; api: KanbanCard }[],
): KanbanFullBoard {
  let next = prev;
  for (const { cardId, api } of updates) {
    next = applyMoveCardApiResponse(next, cardId, api);
  }
  return next;
}

/** Remove outbound `kanban_card` links pointing at `subtaskId` from every card on the board (so only one parent). */
function removeKanbanParentLinkToSubtask(prev: KanbanFullBoard, subtaskId: string): KanbanFullBoard {
  return {
    ...prev,
    cards: prev.cards.map((c) => {
      const links = c.links;
      if (!links?.length) return c;
      const filtered = links.filter((l) => !(l.link_type === 'kanban_card' && l.link_target_id === subtaskId));
      if (filtered.length === links.length) return c;
      return {
        ...c,
        links: filtered.length ? filtered : undefined,
        link_count: filtered.length,
      };
    }),
  };
}

/** Attach `newLink` on `parentId` for this subtask (replace any prior kanban_card link to same target). */
function mergeParentKanbanLinkOnCard(
  prev: KanbanFullBoard,
  parentId: string,
  subtaskId: string,
  newLink: KanbanCardLink,
): KanbanFullBoard {
  return {
    ...prev,
    cards: prev.cards.map((c) => {
      if (c.id !== parentId) return c;
      const existing = Array.isArray(c.links) ? [...c.links] : [];
      const withoutSameTarget = existing.filter(
        (l) => !(l.link_type === 'kanban_card' && l.link_target_id === subtaskId),
      );
      const merged = [...withoutSameTarget, newLink];
      return {
        ...c,
        links: merged,
        link_count: merged.length,
      };
    }),
  };
}

const SWIMLANE_DRAG_MIME = 'application/x-kanban-swimlane-id';

function computeSwimlaneReorderPosition(
  sorted: KanbanSwimlane[],
  dragId: string,
  targetId: string,
  dropAfter: boolean,
): number | null {
  if (dragId === targetId) return null;
  const ids = sorted.map((s) => s.id);
  const without = ids.filter((id) => id !== dragId);
  let insertAt = without.indexOf(targetId);
  if (insertAt < 0) return null;
  if (dropAfter) insertAt += 1;
  const newOrder = [...without.slice(0, insertAt), dragId, ...without.slice(insertAt)];
  const idx = newOrder.indexOf(dragId);
  const posById = new Map(sorted.map((s) => [s.id, s.position]));
  const prevId = idx > 0 ? newOrder[idx - 1] : undefined;
  const nextId = idx < newOrder.length - 1 ? newOrder[idx + 1] : undefined;
  const prevP = prevId !== undefined ? posById.get(prevId) : undefined;
  const nextP = nextId !== undefined ? posById.get(nextId) : undefined;
  if (prevP === undefined && nextP === undefined) return 0;
  if (prevP === undefined) return (nextP ?? 1) - 1;
  if (nextP === undefined) return prevP + 1;
  return (prevP + nextP) / 2;
}

function setKanbanDragImageFromCard(e: React.DragEvent, cardEl: HTMLElement) {
  const rect = cardEl.getBoundingClientRect();
  const clone = cardEl.cloneNode(true) as HTMLElement;
  clone.style.boxSizing = 'border-box';
  clone.style.width = `${rect.width}px`;
  clone.style.maxWidth = `${rect.width}px`;
  clone.style.pointerEvents = 'none';
  clone.style.position = 'fixed';
  clone.style.left = '-9999px';
  clone.style.top = '0';
  clone.style.opacity = '0.96';
  clone.style.transform = 'rotate(2deg)';
  clone.style.boxShadow = '0 18px 40px rgba(0,0,0,0.45)';
  clone.style.borderRadius = '12px';
  clone.style.zIndex = '2147483647';
  document.body.appendChild(clone);
  const ox = Math.min(Math.max(e.clientX - rect.left, 8), rect.width - 8);
  const oy = Math.min(Math.max(e.clientY - rect.top, 8), rect.height - 8);
  e.dataTransfer.setDragImage(clone, ox, oy);
  requestAnimationFrame(() => clone.remove());
}

// ─── FloatingDropdownMenu ────────────────────────────────────────────────────
// Portals to `document.body` with fixed positioning so menus are not clipped or
// covered by later swimlanes / list columns (stacking context + paint order).

function FloatingDropdownMenu({
  open,
  anchorRef,
  onClose,
  children,
  minWidthClass = 'min-w-[160px]',
  align = 'right',
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  minWidthClass?: string;
  align?: 'right' | 'left';
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState({ bottom: 0, left: 0, right: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchorRect({ bottom: r.bottom, left: r.left, right: r.right });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const style: CSSProperties =
    align === 'right'
      ? {
          position: 'fixed',
          top: anchorRect.bottom + 4,
          right: Math.max(8, vw - anchorRect.right),
          left: 'auto',
          zIndex: 9999,
        }
      : {
          position: 'fixed',
          top: anchorRect.bottom + 4,
          left: anchorRect.left,
          zIndex: 9999,
        };

  return createPortal(
    <div
      ref={menuRef}
      style={style}
      className={cn(
        'glass-card rounded-lg py-1 shadow-[var(--shadow-popover)]',
        minWidthClass,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}

// ─── QuickAddCard ────────────────────────────────────────────────────────────

function QuickAddCard({
  listId,
  onAdd,
}: {
  listId: string;
  onAdd: (listId: string, title: string) => void;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) inputRef.current.focus();
  }, [isAdding]);

  if (!isAdding) {
    return (
      <button
        type="button"
        onClick={() => setIsAdding(true)}
        className="btn-ghost btn-xs w-full justify-start"
      >
        <Plus className="w-3.5 h-3.5" />
        Add card
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        className="glass-input w-full text-sm"
        placeholder="Card title..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && title.trim()) {
            onAdd(listId, title);
            setTitle('');
            setIsAdding(false);
          }
          if (e.key === 'Escape') {
            setIsAdding(false);
            setTitle('');
          }
        }}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            if (title.trim()) {
              onAdd(listId, title);
              setTitle('');
              setIsAdding(false);
            }
          }}
          className="glass-button-sm"
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => {
            setIsAdding(false);
            setTitle('');
          }}
          className="glass-button-secondary btn-xs"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── BoardLinkedEventsModal ──────────────────────────────────────────────────
// Event-Workspaces integration: every event bridged to any card on this board.

function BoardLinkedEventsModal({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    eventTeamApiService
      .getEventsForBoard(boardId)
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load linked events');
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  return createPortal(
    <div className="modal-overlay z-[9999]" onClick={onClose}>
      <div
        className="modal-panel max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 className="modal-title inline-flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-accent-400" />
            Linked Events
            <span className="badge badge-neutral">{events?.length ?? '…'}</span>
          </h3>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {error ? (
          <div className="alert alert-danger" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        ) : events == null ? (
          <div className="space-y-1.5" aria-busy="true" aria-label="Loading linked events">
            {[1, 2, 3].map((i) => <SkeletonLoader key={i} className="h-10 w-full" />)}
          </div>
        ) : events.length === 0 ? (
          <p className="text-glass-400 text-sm">No events are bridged to cards on this board.</p>
        ) : (
          <div className="space-y-1.5">
            {events.map((event) => (
              <RouterLink
                key={event.id}
                to={`/event-team/events/${encodeURIComponent(event.id)}`}
                className="subcard flex items-center justify-between gap-2 px-3 py-2 hover:border-glass-200/25 transition-colors group"
              >
                <span className="text-sm text-white truncate group-hover:text-accent-300 transition-colors">
                  {event.title}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <EventStatusBadge status={event.status} type="event" />
                  <ExternalLink className="w-3.5 h-3.5 text-glass-500" />
                </span>
              </RouterLink>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── BoardOverflowMenu ───────────────────────────────────────────────────────
// Power-user menu: groups advanced features behind a single "more" button
// so the toolbar stays clean for simple users.

function BoardOverflowMenu({
  onMetrics, onLabels, onCadences, onSettings, onAccess, onActivity, onStats, onLinkedEvents, onEdit, onArchive, onDelete, showBoardStats,
}: {
  onMetrics: () => void; onLabels?: () => void; onCadences?: () => void; onSettings?: () => void;
  onAccess?: () => void;
  onActivity: () => void; onStats: () => void;
  /** Event-Workspaces integration: list events linked to any card on this board. */
  onLinkedEvents?: () => void;
  onEdit?: () => void; onArchive?: () => void; onDelete?: () => void;
  showBoardStats: boolean;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative" ref={anchorRef}>
      <button type="button" onClick={() => setOpen(v => !v)} className={cn('btn-icon', open && 'bg-accent-500/15 text-accent-300')} title="More options" aria-label="More options" aria-expanded={open}>
        <MoreVertical className="w-4 h-4" />
      </button>
      <FloatingDropdownMenu
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        minWidthClass="min-w-[200px]"
        align="right"
      >
        <div className="eyebrow px-3 py-1.5">Views</div>
        <button type="button" onClick={() => { setOpen(false); onMetrics(); }}
          className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5 shrink-0" /> Metrics Dashboard
        </button>
        <button type="button" onClick={() => { setOpen(false); onActivity(); }}
          className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 shrink-0" /> Activity Feed
        </button>
        <button type="button" onClick={() => { setOpen(false); onStats(); }}
          className={cn('w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2', showBoardStats && 'text-accent-200')}>
          <BarChart3 className="w-3.5 h-3.5 shrink-0" /> Board Stats
        </button>
        {onLinkedEvents && (
          <button type="button" onClick={() => { setOpen(false); onLinkedEvents(); }}
            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
            <PartyPopper className="w-3.5 h-3.5 shrink-0" /> Linked Events
          </button>
        )}

        {(onLabels || onCadences || onSettings || onAccess) && (
          <>
            <div className="border-t border-glass-200/10 my-1" />
            <div className="eyebrow px-3 py-1.5">Manage</div>
            {onAccess && (
              <button type="button" onClick={() => { setOpen(false); onAccess(); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
                <User className="w-3.5 h-3.5 shrink-0" /> Board access
              </button>
            )}
            {onLabels && (
              <button type="button" onClick={() => { setOpen(false); onLabels(); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 shrink-0" /> Labels
              </button>
            )}
            {onCadences && (
              <button type="button" onClick={() => { setOpen(false); onCadences(); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5 shrink-0" /> Cadences
              </button>
            )}
            {onSettings && (
              <button type="button" onClick={() => { setOpen(false); onSettings(); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
                <Settings className="w-3.5 h-3.5 shrink-0" /> Board Settings
              </button>
            )}
          </>
        )}

        {(onEdit || onArchive || onDelete) && (
          <>
            <div className="border-t border-glass-200/10 my-1" />
            <div className="eyebrow px-3 py-1.5">Board</div>
            {onEdit && (
              <button type="button" onClick={() => { setOpen(false); onEdit(); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2">
                <Edit3 className="w-3.5 h-3.5 shrink-0" /> Edit Board
              </button>
            )}
            {onArchive && (
              <button type="button" onClick={() => { setOpen(false); onArchive(); }}
                className="w-full text-left px-3 py-2 text-sm text-amber-300 hover:bg-glass-100/10 flex items-center gap-2">
                <Archive className="w-3.5 h-3.5 shrink-0" /> Archive Board
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={() => { setOpen(false); onDelete(); }}
                className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-glass-100/10 flex items-center gap-2">
                <Trash2 className="w-3.5 h-3.5 shrink-0" /> Delete Board
              </button>
            )}
          </>
        )}
      </FloatingDropdownMenu>
    </div>
  );
}

// ─── ListHeaderMenu ──────────────────────────────────────────────────────────

function ListHeaderMenu({
  listIndex,
  totalLists,
  onEdit,
  onMoveLeft,
  onMoveRight,
  onArchive,
  onDelete,
  onBatchMoveTo,
  otherLists,
}: {
  listIndex: number;
  totalLists: number;
  onEdit: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onBatchMoveTo?: (targetListId: string) => void;
  otherLists?: { id: string; title: string }[];
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative" ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-icon btn-icon-sm"
        title="List actions"
        aria-label="List actions"
        aria-expanded={open}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      <FloatingDropdownMenu open={open} anchorRef={anchorRef} onClose={() => setOpen(false)} minWidthClass="min-w-[160px]" align="right">
        <button
          type="button"
          onClick={() => { setOpen(false); onEdit(); }}
          className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2"
        >
          <Pencil className="w-3.5 h-3.5 shrink-0" /> Edit List
        </button>
        <button
          type="button"
          disabled={listIndex === 0}
          onClick={() => { setOpen(false); onMoveLeft(); }}
          className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2 disabled:opacity-40"
        >
          <ChevronLeft className="w-3.5 h-3.5 shrink-0" /> Move Left
        </button>
        <button
          type="button"
          disabled={listIndex === totalLists - 1}
          onClick={() => { setOpen(false); onMoveRight(); }}
          className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2 disabled:opacity-40"
        >
          <ChevronRight className="w-3.5 h-3.5 shrink-0" /> Move Right
        </button>
        {onBatchMoveTo && otherLists && otherLists.length > 0 && (
          <>
            <div className="border-t border-glass-200/10 my-1" />
            <div className="eyebrow px-3 py-1.5">Move all cards to</div>
            {otherLists.map(ol => (
              <button key={ol.id} type="button"
                onClick={() => { setOpen(false); onBatchMoveTo(ol.id); }}
                className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 truncate"
              >
                → {ol.title}
              </button>
            ))}
          </>
        )}
        <div className="border-t border-glass-200/10 my-1" />
        <button
          type="button"
          onClick={() => { setOpen(false); onArchive(); }}
          className="w-full text-left px-3 py-1.5 text-sm text-amber-300 hover:bg-glass-100/10 flex items-center gap-2"
        >
          <Archive className="w-3.5 h-3.5 shrink-0" /> Archive List
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); onDelete(); }}
          className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-glass-100/10 flex items-center gap-2"
        >
          <Trash2 className="w-3.5 h-3.5 shrink-0" /> Delete List
        </button>
      </FloatingDropdownMenu>
    </div>
  );
}

// ─── SwimlaneHeader ──────────────────────────────────────────────────────────

function SwimlaneHeader({
  swimlane,
  cardCount,
  isCollapsed,
  canWrite,
  canManage,
  swimlaneIndex,
  totalSwimlanes,
  onToggleCollapse,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  swimlaneDragActive,
  onSwimlaneDragStart,
  onSwimlaneDragEnd,
  onSwimlaneDragOver,
  onSwimlaneDragLeave,
  onSwimlaneDrop,
}: {
  swimlane: KanbanSwimlane;
  cardCount: number;
  isCollapsed: boolean;
  canWrite: boolean;
  canManage: boolean;
  swimlaneIndex: number;
  totalSwimlanes: number;
  onToggleCollapse: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  swimlaneDragActive: boolean;
  onSwimlaneDragStart: (e: React.DragEvent) => void;
  onSwimlaneDragEnd: () => void;
  onSwimlaneDragOver: (e: React.DragEvent) => void;
  onSwimlaneDragLeave: (e: React.DragEvent) => void;
  onSwimlaneDrop: (e: React.DragEvent) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cn(
        'flex items-center gap-2 py-1.5 px-2.5 glass-card mb-1.5 transition-shadow',
        swimlaneDragActive && 'ring-1 ring-accent-400/35',
      )}
      onDragOver={onSwimlaneDragOver}
      onDragLeave={onSwimlaneDragLeave}
      onDrop={(e) => void onSwimlaneDrop(e)}
    >
      {canManage && (
        <div
          draggable
          title="Drag to reorder swimlane"
          onDragStart={onSwimlaneDragStart}
          onDragEnd={onSwimlaneDragEnd}
          className="flex-shrink-0 p-1 rounded cursor-grab active:cursor-grabbing text-glass-500 hover:text-accent-300 hover:bg-glass-100/10 touch-none select-none"
          aria-label="Reorder swimlane"
        >
          <GripVertical className="w-4 h-4" />
        </div>
      )}
      {swimlane.color && (
        <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: swimlane.color }} />
      )}
      <button
        type="button"
        onClick={canWrite ? onToggleCollapse : undefined}
        className={cn('btn-icon btn-icon-sm', !canWrite && 'cursor-default hover:bg-transparent')}
        aria-label={isCollapsed ? 'Expand swimlane' : 'Collapse swimlane'}
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      <h3 className="text-sm font-semibold text-white flex-1 truncate">{swimlane.title}</h3>
      <span className="badge badge-neutral">{cardCount} cards</span>
      {canManage && (
        <div className="relative" ref={anchorRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="btn-icon btn-icon-sm"
            title="Swimlane actions"
            aria-label="Swimlane actions"
            aria-expanded={menuOpen}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
          <FloatingDropdownMenu
            open={menuOpen}
            anchorRef={anchorRef}
            onClose={() => setMenuOpen(false)}
            minWidthClass="min-w-[150px]"
            align="right"
          >
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onEdit(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2"
            >
              <Pencil className="w-3.5 h-3.5 shrink-0" /> Edit
            </button>
            <button
              type="button"
              disabled={swimlaneIndex === 0}
              onClick={() => { setMenuOpen(false); onMoveUp(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2 disabled:opacity-40"
            >
              <ChevronUp className="w-3.5 h-3.5 shrink-0" /> Move Up
            </button>
            <button
              type="button"
              disabled={swimlaneIndex === totalSwimlanes - 1}
              onClick={() => { setMenuOpen(false); onMoveDown(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-glass-100/10 flex items-center gap-2 disabled:opacity-40"
            >
              <ChevronDown className="w-3.5 h-3.5 shrink-0" /> Move Down
            </button>
            <div className="border-t border-glass-200/10 my-1" />
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onDelete(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-glass-100/10 flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5 shrink-0" /> Delete
            </button>
          </FloatingDropdownMenu>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function WorkspaceBoardView() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useDocumentTitle('Board');
  const { identity, isLoading: authLoading } = useAuth();
  const isDev = import.meta.env.DEV;
  const isMobile = useIsMobile();
  const [boardData, setBoardData] = useState<KanbanFullBoard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const boardReqIdRef = useRef(0);
  const boardDataRef = useRef<KanbanFullBoard | null>(null);
  const syncSummaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<KanbanCardType | ''>('');
  const [classFilter, setClassFilter] = useState<KanbanClassOfService | ''>('');
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  /** Default columns; optional swimlanes layout when the board defines swimlanes. */
  const [boardColumnLayout, setBoardColumnLayout] = useState<BoardColumnLayout>(() => readStoredBoardLayout());
  const [selectedCardId, setSelectedCardId] = useState<string | null>(() => searchParams.get('card'));
  const [showFilters, setShowFilters] = useState(false);
  type ActiveDragState = {
    cardId: string;
    /** `stack` = root + linked descendants; `solo` = one nested subtask only (reparent / move alone). */
    dragMode: 'stack' | 'solo';
    /** All card ids that move with this drag (root first, then nested subtasks in list order). */
    movedCardIds: string[];
    sourceListId: string;
    /** Swimlane bucket key (`__unsorted__` or swimlane id); omitted on flat boards. */
    sourceSwimlaneBucket?: string;
    pointerColumnKey: string | null;
    insertIndex: number | null;
  };
  const [activeDrag, setActiveDrag] = useState<ActiveDragState | null>(null);
  /** True only between card native dragstart and dragend (drop targets may clear `activeDrag` early). */
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const [draggedSwimlaneId, setDraggedSwimlaneId] = useState<string | null>(null);
  /** Flat card order per column (root then nested for each stack) for drag insert + positions. */
  const listCardFlatOrderRef = useRef<Record<string, string[]>>({});

  // Modal state
  const [showEditBoard, setShowEditBoard] = useState(false);
  const [showBoardAccess, setShowBoardAccess] = useState(false);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [showCreateSwimlane, setShowCreateSwimlane] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showAddLink, setShowAddLink] = useState<string | null>(null);
  const [showDeleteBoard, setShowDeleteBoard] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  // Feature 2: Activity sidebar
  const [showActivity, setShowActivity] = useState(false);

  // Event-Workspaces integration: "Linked Events" board modal (lead + kanban read).
  const [showLinkedEvents, setShowLinkedEvents] = useState(false);
  const [activityEntries, setActivityEntries] = useState<KanbanActivityLogEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Feature 4: List header actions
  const [editListTarget, setEditListTarget] = useState<KanbanList | null>(null);
  const [deleteListTarget, setDeleteListTarget] = useState<string | null>(null);
  const [deleteListLoading, setDeleteListLoading] = useState(false);

  // Feature 5: Board-level views
  const [viewPanel, setViewPanel] = useState<'my_cards' | 'overdue' | 'search' | 'due_range' | 'by_actor' | null>(null);
  const [viewPanelCards, setViewPanelCards] = useState<(KanbanCardSummary | KanbanCard)[]>([]);
  const [viewPanelLoading, setViewPanelLoading] = useState(false);
  const [viewSearchQuery, setViewSearchQuery] = useState('');

  // Feature 6: Archive board
  const [archiveBoardLoading, setArchiveBoardLoading] = useState(false);
  const [showArchiveBoard, setShowArchiveBoard] = useState(false);

  // Feature 7: Swimlane management
  const [editSwimlaneTarget, setEditSwimlaneTarget] = useState<KanbanSwimlane | null>(null);
  const [editSwimlaneTitleVal, setEditSwimlaneTitleVal] = useState('');
  const [editSwimlaneColorVal, setEditSwimlaneColorVal] = useState('');
  const [editSwimlaneSubmitting, setEditSwimlaneSubmitting] = useState(false);
  const [deleteSwimlaneTarget, setDeleteSwimlaneTarget] = useState<string | null>(null);
  const [deleteSwimlaneLoading, setDeleteSwimlaneLoading] = useState(false);
  const [collapsedSwimlanes, setCollapsedSwimlanes] = useState<Set<string>>(new Set());

  // Feature 8: Board stats
  const [boardStats, setBoardStats] = useState<KanbanStats | null>(null);
  const [boardStatsLoading, setBoardStatsLoading] = useState(false);
  const [showBoardStats, setShowBoardStats] = useState(false);

  // Feature 9: Upcoming cadences
  const [showCadencesPanel, setShowCadencesPanel] = useState(false);
  const [cadencesList, setCadencesList] = useState<KanbanCadence[]>([]);
  const [cadencesLoading, setCadencesLoading] = useState(false);

  // Feature 10: Cadence management
  const [editCadenceTarget, setEditCadenceTarget] = useState<KanbanCadence | null>(null);
  const [showCadenceCreate, setShowCadenceCreate] = useState(false);
  const [deleteCadenceTarget, setDeleteCadenceTarget] = useState<string | null>(null);
  const [deleteCadenceLoading, setDeleteCadenceLoading] = useState(false);

  /** Drag-to-delete: hover state on the bottom trash drop target. */
  const [cardDeleteDropHover, setCardDeleteDropHover] = useState(false);
  const [deleteCardTarget, setDeleteCardTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteCardLoading, setDeleteCardLoading] = useState(false);

  /** Bumped when WS signals `refetch_card_detail` for the open card (`KanbanCardDetailPanel`). */
  const [wsDetailRefetchNonce, setWsDetailRefetchNonce] = useState(0);
  /**
   * Bumped when the board WS signals `card.incident_linked` / `card.incident_unlinked` for the
   * currently open card so the panel's `<CardIncidentsSection>` refetches its incident list.
   */
  const [wsCardIncidentsNonce, setWsCardIncidentsNonce] = useState(0);
  /** Same for `card.event_linked` → the panel's `<CardEventsSection>`. */
  const [wsCardEventsNonce, setWsCardEventsNonce] = useState(0);
  const showCadencesPanelRef = useRef(false);

  const debouncedSearch = useDebounce(searchFilter, 300);

  useEffect(() => {
    try {
      localStorage.setItem(BOARD_LAYOUT_STORAGE_KEY, boardColumnLayout);
    } catch {
      /* quota / private mode */
    }
  }, [boardColumnLayout]);

  // Permission levels per the documented hierarchy: manage ⊃ write ⊃ read
  const { read: canRead, write: canWrite, manage: canManage, manage_access: canManageAccess } = useMemo(
    () => getKanbanBoardPermissions(identity, isDev, boardData?.board),
    [identity, isDev, boardData?.board],
  );

  /**
   * "Linked Events" board lookup (`GET /kanban/boards/{id}/events`) requires
   * event-team lead + kanban access for this board (Event-Workspaces integration).
   */
  const canSeeLinkedEvents = useMemo(
    () => getEventTeamPerms(identity, isDev).canLead,
    [identity, isDev],
  );

  const prefetchUsernamesForBoard = useCallback((data: KanbanFullBoard) => {
    const uuids = new Set<string>();
    data.cards.forEach((c) => {
      if (c.created_by) uuids.add(c.created_by);
      (Array.isArray(c.assignees) ? c.assignees : []).forEach((a) => {
        if (a?.user_uuid) uuids.add(a.user_uuid);
      });
    });
    void usernameCacheService.prefetchUsernames(Array.from(uuids));
  }, []);

  const loadBoard = useCallback(
    async (opts: { mode: 'initial' | 'silent' }) => {
      if (!boardId) return;
      const reqId = ++boardReqIdRef.current;
      const isInitial = opts.mode === 'initial';

      if (isInitial) {
        setIsLoading(true);
        setError(null);
        setSyncSummary(null);
        if (syncSummaryTimerRef.current) {
          clearTimeout(syncSummaryTimerRef.current);
          syncSummaryTimerRef.current = null;
        }
      } else {
        setBackgroundRefreshing(true);
      }

      try {
        const data = await kanbanApiService.getFullBoard(boardId);
        if (reqId !== boardReqIdRef.current) return;

        if (isInitial) {
          const normalizedInitial = normalizeFullBoardArrays(data, boardId);
          setBoardData(normalizedInitial);
          prefetchUsernamesForBoard(normalizedInitial);
        } else {
          const prev = boardDataRef.current;
          if (prev) {
            const msg = safeBuildBoardSyncSummary(prev, data);
            if (msg) {
              if (syncSummaryTimerRef.current) clearTimeout(syncSummaryTimerRef.current);
              setSyncSummary(msg);
              syncSummaryTimerRef.current = setTimeout(() => {
                setSyncSummary(null);
                syncSummaryTimerRef.current = null;
              }, 4500);
            }
          }
          const normalized = normalizeFullBoardArrays(data, boardId);
          setBoardData(normalized);
          prefetchUsernamesForBoard(normalized);
        }
      } catch (err) {
        if (reqId !== boardReqIdRef.current) return;
        if (err instanceof KanbanApiError && [401, 403, 404].includes(err.status)) {
          setBoardData(null);
          setSelectedCardId(null);
          setShowBoardAccess(false);
          setError(err.status === 404 ? 'This board is no longer available.' : 'You no longer have permission to view this board.');
          return;
        }
        if (isInitial) {
          try {
            const [lists, swimlanes, labels] = await Promise.all([
              kanbanApiService.listLists(boardId),
              kanbanApiService.listSwimlanes(boardId),
              kanbanApiService.listLabels(boardId),
            ]);
            const cardsRes = await kanbanApiService.listCards(boardId, { limit: 200 });
            const board = await kanbanApiService.getBoard(boardId);
            const settings = await kanbanApiService.listBoardSettings(boardId);
            if (reqId !== boardReqIdRef.current) return;
            const composed: KanbanFullBoard = {
              board,
              lists,
              swimlanes,
              labels,
              settings,
              cards: (Array.isArray(cardsRes.results) ? cardsRes.results : []).map((c) => ({
                ...c,
                labels: [],
                assignees: [],
                checklist_progress: { total: 0, checked: 0 },
                comment_count: 0,
                attachment_count: 0,
                watcher_count: 0,
                link_count: 0,
              })),
            };
            const normalized = normalizeFullBoardArrays(composed);
            setBoardData(normalized);
            prefetchUsernamesForBoard(normalized);
          } catch {
            setError(err instanceof Error ? err.message : 'Failed to load board');
          }
        } else {
          try {
            const [lists, swimlanes, labels] = await Promise.all([
              kanbanApiService.listLists(boardId),
              kanbanApiService.listSwimlanes(boardId),
              kanbanApiService.listLabels(boardId),
            ]);
            const cardsRes = await kanbanApiService.listCards(boardId, { limit: 200 });
            const board = await kanbanApiService.getBoard(boardId);
            const settings = await kanbanApiService.listBoardSettings(boardId);
            if (reqId !== boardReqIdRef.current) return;
            const composed: KanbanFullBoard = {
              board,
              lists,
              swimlanes,
              labels,
              settings,
              cards: (Array.isArray(cardsRes.results) ? cardsRes.results : []).map((c) => ({
                ...c,
                labels: [],
                assignees: [],
                checklist_progress: { total: 0, checked: 0 },
                comment_count: 0,
                attachment_count: 0,
                watcher_count: 0,
                link_count: 0,
              })),
            };
            const prev = boardDataRef.current;
            if (prev) {
              const msg = safeBuildBoardSyncSummary(prev, composed);
              if (msg) {
                if (syncSummaryTimerRef.current) clearTimeout(syncSummaryTimerRef.current);
                setSyncSummary(msg);
                syncSummaryTimerRef.current = setTimeout(() => {
                  setSyncSummary(null);
                  syncSummaryTimerRef.current = null;
                }, 4500);
              }
            }
            const normalized = normalizeFullBoardArrays(composed);
            setBoardData(normalized);
            prefetchUsernamesForBoard(normalized);
          } catch (composeErr) {
            console.error('Background board refresh failed:', composeErr);
          }
        }
      } finally {
        if (reqId !== boardReqIdRef.current) return;
        if (isInitial) setIsLoading(false);
        else setBackgroundRefreshing(false);
      }
    },
    [boardId, prefetchUsernamesForBoard],
  );

  const refreshBoard = useCallback(async () => {
    await loadBoard({ mode: 'silent' });
  }, [loadBoard]);

  const openCardDetail = useCallback(
    (id: string) => {
      setSelectedCardId(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('card', id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeCardDetail = useCallback(() => {
    setSelectedCardId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('card');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const syncBoardCardFromFull = useCallback((full: KanbanCardFull) => {
    setBoardData((prev) => {
      if (!prev) return prev;
      const summary = cardFullToBoardCardSummary(full);
      const nextCards = prev.cards.map((c) => (c.id === summary.id ? summary : c));
      return { ...prev, cards: nextCards };
    });
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get('card');
    setSelectedCardId((prev) => (fromUrl === prev ? prev : fromUrl));
  }, [searchParams]);

  useEffect(() => {
    boardDataRef.current = boardData;
  }, [boardData]);

  useEffect(() => {
    if (!boardId) return;
    void loadBoard({ mode: 'initial' });
    const pollMs = 120_000;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void loadBoard({ mode: 'silent' });
    }, pollMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadBoard({ mode: 'silent' });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      if (syncSummaryTimerRef.current) {
        clearTimeout(syncSummaryTimerRef.current);
        syncSummaryTimerRef.current = null;
      }
    };
  }, [boardId, loadBoard]);

  // Initialize collapsed swimlane state from board data
  useEffect(() => {
    if (!boardData) return;
    const collapsed = new Set<string>();
    boardData.swimlanes.forEach((sw) => {
      if (sw.is_collapsed === 1) collapsed.add(sw.id);
    });
    setCollapsedSwimlanes(collapsed);
  }, [boardData]);

  const boardSettingsRows = useMemo(
    () => coerceKanbanBoardSettings(boardData?.settings),
    [boardData?.settings],
  );

  const wipWarningHours = useMemo(() => {
    const raw = boardSettingsRows.find((s) => s.setting_key === 'wip_aging_warning_hours')?.setting_value;
    const n = raw !== undefined ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 48;
  }, [boardSettingsRows]);

  const filteredCards = useMemo(() => {
    const cards = boardData?.cards ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    return cards.filter((c) => {
      if (q) {
        const inTitle = c.title.toLowerCase().includes(q);
        const inDesc = c.description?.toLowerCase().includes(q) ?? false;
        if (!inTitle && !inDesc) return false;
      }
      if (typeFilter && c.card_type !== typeFilter) return false;
      if (classFilter && c.class_of_service !== classFilter) return false;
      if (showBlockedOnly && c.is_blocked !== 1) return false;
      return true;
    });
  }, [boardData, debouncedSearch, typeFilter, classFilter, showBlockedOnly]);

  const sortedLists = useMemo(
    () => [...(boardData?.lists || [])].sort((a, b) => a.position - b.position),
    [boardData],
  );

  const sortedSwimlanes = useMemo(
    () => [...(boardData?.swimlanes || [])].sort((a, b) => a.position - b.position),
    [boardData],
  );

  const hasSwimlanes = sortedSwimlanes.length > 0;
  const useSwimlaneBuckets =
    hasSwimlanes && boardColumnLayout === 'swimlanes';

  const swimlaneTitleById = useMemo(() => {
    const m = new Map<string, string>();
    sortedSwimlanes.forEach((s) => m.set(s.id, s.title));
    return m;
  }, [sortedSwimlanes]);

  const swimlaneColorById = useMemo(() => {
    const m = new Map<string, string | null>();
    sortedSwimlanes.forEach((s) => m.set(s.id, s.color));
    return m;
  }, [sortedSwimlanes]);

  const cardsByList = useMemo(() => {
    const map: Record<string, KanbanCardSummary[]> = {};
    sortedLists.forEach((l) => {
      map[l.id] = [];
    });
    filteredCards.forEach((c) => {
      if (map[c.list_id]) map[c.list_id].push(c);
    });
    Object.values(map).forEach((cards) => cards.sort((a, b) => a.position - b.position));
    return map;
  }, [sortedLists, filteredCards]);

  const cardsByListAndSwimlane = useMemo(() => {
    if (!useSwimlaneBuckets) return null;
    const map: Record<string, Record<string, KanbanCardSummary[]>> = {};
    const swimlaneKeys = [...sortedSwimlanes.map((s) => s.id), '__unsorted__'];
    sortedLists.forEach((l) => {
      map[l.id] = {};
      swimlaneKeys.forEach((sk) => { map[l.id][sk] = []; });
    });
    filteredCards.forEach((c) => {
      if (!map[c.list_id]) return;
      const sk = c.swimlane_id ?? '__unsorted__';
      if (!map[c.list_id][sk]) map[c.list_id][sk] = [];
      map[c.list_id][sk].push(c);
    });
    Object.values(map).forEach((bySwim) =>
      Object.values(bySwim).forEach((cards) => cards.sort((a, b) => a.position - b.position)),
    );
    return map;
  }, [useSwimlaneBuckets, sortedLists, sortedSwimlanes, filteredCards]);

  // ── Keyboard shortcuts for power users ─────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (e.key.toLowerCase()) {
        case 'n': if (canWrite) { e.preventDefault(); setShowCreateCard(true); } break;
        case 'f': e.preventDefault(); setShowFilters(f => !f); break;
        case 'r': e.preventDefault(); void refreshBoard(); break;
        case 'l':
          if (useSwimlaneBuckets || (boardData?.swimlanes?.length ?? 0) > 0) {
            e.preventDefault();
            setBoardColumnLayout((prev) => (prev === 'columns' ? 'swimlanes' : 'columns'));
          }
          break;
        case 'escape':
          if (selectedCardId) { closeCardDetail(); }
          else if (showFilters) { setShowFilters(false); }
          else if (viewPanel) { setViewPanel(null); }
          break;
        case '?':
          if (e.shiftKey) { e.preventDefault(); setShowShortcutsHelp(h => !h); }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    canWrite,
    selectedCardId,
    showFilters,
    viewPanel,
    refreshBoard,
    closeCardDetail,
    useSwimlaneBuckets,
    boardData?.swimlanes?.length,
  ]);

  // ─── Board Activity (Feature 2) ────────────────────────────────────────────

  const loadActivity = useCallback(async () => {
    if (!boardId) return;
    setActivityLoading(true);
    try {
      const res = await kanbanApiService.getBoardActivity(boardId, { limit: 50 });
      setActivityEntries(res.results);
    } catch (e) {
      console.error('Failed to load activity:', e);
    } finally {
      setActivityLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (showActivity) void loadActivity();
  }, [showActivity, loadActivity]);

  // ─── Board Stats (Feature 8) ──────────────────────────────────────────────

  const loadBoardStats = useCallback(async () => {
    if (!boardId) return;
    setBoardStatsLoading(true);
    try {
      const stats = await kanbanApiService.getBoardStats(boardId);
      setBoardStats(stats);
    } catch (e) {
      console.error('Failed to load board stats:', e);
    } finally {
      setBoardStatsLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (showBoardStats) {
      void loadBoardStats();
    }
  }, [showBoardStats, loadBoardStats]);

  // ─── Cadences Panel (Feature 9 + 10) ──────────────────────────────────────

  const loadCadences = useCallback(async () => {
    if (!boardId) return;
    setCadencesLoading(true);
    try {
      const list = await kanbanApiService.listCadences(boardId);
      setCadencesList(list);
    } catch (e) {
      console.error('Failed to load cadences:', e);
    } finally {
      setCadencesLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (showCadencesPanel) void loadCadences();
  }, [showCadencesPanel, loadCadences]);

  useEffect(() => {
    showCadencesPanelRef.current = showCadencesPanel;
  }, [showCadencesPanel]);

  const loadCadencesRef = useRef(loadCadences);
  useEffect(() => {
    loadCadencesRef.current = loadCadences;
  }, [loadCadences]);

  const handleKanbanWsEnvelope = useCallback(
    (env: KanbanWsEnvelope) => {
      if (env.type === 'system.error') {
        console.warn('Kanban WS:', (env.payload as { message?: string } | undefined)?.message ?? env);
        return;
      }
      if (env.type.startsWith('system.')) return;

      if (env.type === 'board.deleted' && env.board_id && boardId && env.board_id === boardId) {
        navigate('/workspaces');
        return;
      }

      if (env.type.startsWith('cadence.') && showCadencesPanelRef.current && boardId) {
        void loadCadencesRef.current();
      }

      // Incident-Workspaces bridge events on the board room. These don't mutate any list/card
      // shape but they DO mean the open card's linked-incidents list may have changed.
      if (env.type === 'card.incident_linked' || env.type === 'card.incident_unlinked') {
        const payloadCardId =
          env.payload && typeof env.payload === 'object'
            ? (env.payload as { card_id?: unknown }).card_id
            : undefined;
        if (
          typeof payloadCardId === 'string' &&
          selectedCardId &&
          payloadCardId === selectedCardId
        ) {
          setWsCardIncidentsNonce((n) => n + 1);
        }
        return;
      }

      // Event-Workspaces bridge events (events are linked/promoted onto a card).
      if (env.type === 'card.event_linked' || env.type === 'card.event_unlinked') {
        const payloadCardId =
          env.payload && typeof env.payload === 'object'
            ? (env.payload as { card_id?: unknown }).card_id
            : undefined;
        if (
          typeof payloadCardId === 'string' &&
          selectedCardId &&
          payloadCardId === selectedCardId
        ) {
          setWsCardEventsNonce((n) => n + 1);
        }
        return;
      }

      setBoardData((prev) => {
        if (!prev || !boardId) return prev;
        const { next, healFull, refetchCardDetailId } = mergeKanbanBoardEvent(prev, env);
        if (healFull) {
          queueMicrotask(() => void loadBoard({ mode: 'silent' }));
          return prev;
        }
        const normalized = normalizeFullBoardArrays(next, boardId);
        if (env.activity) {
          setActivityEntries((entries) => {
            const a = env.activity!;
            if (entries.some((e) => e.id === a.id)) return entries;
            return [a, ...entries].slice(0, 80);
          });
        }
        if (refetchCardDetailId && selectedCardId === refetchCardDetailId) {
          setWsDetailRefetchNonce((n) => n + 1);
        }
        void usernameCacheService.prefetchUsernames(
          normalized.cards.flatMap((c) => [c.created_by, ...(c.assignees ?? []).map((x) => x.user_uuid)]).filter(Boolean) as string[],
        );
        return normalized;
      });
    },
    [boardId, navigate, loadBoard, selectedCardId],
  );

  const handleKanbanWsSeqGap = useCallback(() => {
    void loadBoard({ mode: 'silent' });
  }, [loadBoard]);

  const wsEnabled =
    Boolean(boardId) &&
    !authLoading &&
    (isDev || (identity != null && canRead));

  useKanbanBoardWebSocket({
    boardId,
    enabled: wsEnabled,
    onEnvelope: handleKanbanWsEnvelope,
    onSeqGap: handleKanbanWsSeqGap,
    onAccessChanged: () => void refreshBoard(),
  });

  // ─── View Panel handlers (Feature 5) ──────────────────────────────────────

  const loadMyCards = useCallback(async () => {
    if (!identity?.user_uuid) return;
    setViewPanelLoading(true);
    try {
      const res = await kanbanApiService.getCardsByAssignee(identity.user_uuid, {
        team: boardData?.board?.team,
      });
      setViewPanelCards(res.results);
    } catch (e) {
      console.error(e);
    } finally {
      setViewPanelLoading(false);
    }
  }, [identity, boardData]);

  const loadOverdueCards = useCallback(async () => {
    if (!boardId) return;
    setViewPanelLoading(true);
    try {
      const res = await kanbanApiService.getOverdueCards(boardId, { limit: 50 });
      setViewPanelCards(res.results);
    } catch (e) {
      console.error(e);
    } finally {
      setViewPanelLoading(false);
    }
  }, [boardId]);

  // Reverse link lookup — used when searching for linked entities on the board
  const reverseCardLinkLookup = useCallback(async (linkType: string, targetId: string) => {
    try {
      return await kanbanApiService.reverseCardLinkLookup(linkType, targetId);
    } catch (e) { console.error(e); return []; }
  }, []);

  const handleSearchCards = useCallback(async (q: string) => {
    if (!q.trim()) { setViewPanelCards([]); return; }
    setViewPanelLoading(true);
    try {
      const [searchRes, linkedRes] = await Promise.allSettled([
        kanbanApiService.searchCards({ q, board_id: boardId }),
        // Also check if the search term matches a player UUID (reverse link lookup)
        q.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
          ? reverseCardLinkLookup('player', q)
          : Promise.resolve([]),
      ]);
      const cards = searchRes.status === 'fulfilled' ? searchRes.value.results : [];
      // If reverse lookup found linked cards, add them to results
      if (linkedRes.status === 'fulfilled' && Array.isArray(linkedRes.value) && linkedRes.value.length > 0) {
        const linkedCardIds = new Set(linkedRes.value.map(l => l.card_id));
        const linkedBoardCards = (boardData?.cards || []).filter(c => linkedCardIds.has(c.id));
        const existingIds = new Set(cards.map(c => c.id));
        const newCards = linkedBoardCards.filter(c => !existingIds.has(c.id));
        setViewPanelCards([...cards, ...newCards]);
      } else {
        setViewPanelCards(cards);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setViewPanelLoading(false);
    }
  }, [boardId, reverseCardLinkLookup, boardData]);

  const loadDueRangeCards = useCallback(async (from: number, to: number) => {
    if (!boardId) return;
    setViewPanelLoading(true);
    try {
      const res = await kanbanApiService.getCardsByDueRange(boardId, from, to, { limit: 100 });
      setViewPanelCards(res.results);
    } catch (e) { console.error(e); }
    finally { setViewPanelLoading(false); }
  }, [boardId]);

  const loadActivityByActor = useCallback(async (actorId: string) => {
    if (!actorId) return;
    try {
      const res = await kanbanApiService.getActivityByActor(actorId, { limit: 50 });
      setActivityEntries(res.results);
    } catch (e) { console.error(e); }
  }, []);

  const handleBatchMove = useCallback(async (cardIds: string[], targetListId: string) => {
    if (!boardId || cardIds.length === 0) return;
    try {
      await kanbanApiService.batchMoveCards(boardId, { card_ids: cardIds, list_id: targetListId });
      void refreshBoard();
    } catch (e) { console.error('Batch move failed:', e); }
  }, [boardId, refreshBoard]);

  const [cardCountByList, setCardCountByList] = useState<{ list_id: string; list_title: string; card_count: number }[]>([]);
  const loadCardCountByList = useCallback(async () => {
    if (!boardId) return;
    try {
      const data = await kanbanApiService.getCardCountByList(boardId);
      setCardCountByList(data);
    } catch (e) { console.error(e); }
  }, [boardId]);

  // Load card count by list alongside board stats  
  useEffect(() => {
    if (showBoardStats) void loadCardCountByList();
  }, [showBoardStats, loadCardCountByList]);

  const openViewPanel = (panel: typeof viewPanel) => {
    setViewPanel(panel);
    setViewPanelCards([]);
    if (panel === 'my_cards') void loadMyCards();
    if (panel === 'overdue') void loadOverdueCards();
    if (panel === 'due_range') {
      // Default to next 7 days
      const now = Date.now();
      const weekLater = now + 7 * 86400000;
      void loadDueRangeCards(now, weekLater);
    }
  };

  // ─── Drag & Drop (Trello-style preview + reorder within column) ─────────────

  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    if (!boardData) return;
    const card = boardData.cards.find((c) => c.id === cardId);
    if (!card) return;
    setIsDraggingCard(true);
    const swimBucket = useSwimlaneBuckets ? (card.swimlane_id ?? '__unsorted__') : undefined;
    const colKey = columnRefKey(card.list_id, swimBucket, useSwimlaneBuckets);
    const flatIds = listCardFlatOrderRef.current[colKey] ?? [];
    const descendantMap = buildDescendantIdsByCardId(boardData.cards, swimBucket);
    const desc = descendantMap.get(cardId) ?? new Set<string>();
    const bundleIds = new Set<string>([cardId, ...desc]);
    const colCards = boardData.cards
      .filter((c) => {
        if (c.list_id !== card.list_id) return false;
        if (useSwimlaneBuckets) {
          const sk = c.swimlane_id ?? '__unsorted__';
          return sk === (swimBucket ?? '__unsorted__');
        }
        return true;
      })
      .sort((a, b) => a.position - b.position);

    const stackRowsHere = buildBoardCardStackRows(colCards, descendantMap);
    const rowForDrag = stackRowForDraggedCardId(stackRowsHere, cardId);
    const isSoloNestedSubtask =
      card.card_type === 'subtask' &&
      rowForDrag?.kind === 'single' &&
      stackRowsHere.some((r) => r.kind === 'stack' && r.nested.some((s) => s.id === cardId));

    const movedCardIds = isSoloNestedSubtask
      ? [cardId]
      : colCards.map((c) => c.id).filter((id) => bundleIds.has(id));

    const previewRow = stackRowForDraggedCardId(stackRowsHere, cardId);
    const primaryFlatId = previewRow
      ? previewRow.kind === 'single'
        ? previewRow.card.id
        : previewRow.root.id
      : cardId;
    const flatInsert = flatIds.indexOf(primaryFlatId);
    const insertAt = flatInsert >= 0 ? flatInsert : 0;

    setActiveDrag({
      cardId,
      dragMode: isSoloNestedSubtask ? 'solo' : 'stack',
      movedCardIds,
      sourceListId: card.list_id,
      sourceSwimlaneBucket: swimBucket,
      pointerColumnKey: colKey,
      insertIndex: insertAt >= 0 ? insertAt : 0,
    });
    const target = e.currentTarget as HTMLElement;
    const stackEl = target.closest(KANBAN_STACK_ROOT_SEL) as HTMLElement | null;
    const cardEl = (target.closest(KANBAN_CARD_SEL) as HTMLElement | null) ?? stackEl;
    if (cardEl) setKanbanDragImageFromCard(e, cardEl);
  };

  const handleCardDragEnd = () => {
    setIsDraggingCard(false);
    setActiveDrag(null);
    setCardDeleteDropHover(false);
  };

  useEffect(() => {
    const endCardDrag = () => {
      setIsDraggingCard(false);
      setCardDeleteDropHover(false);
    };
    window.addEventListener('dragend', endCardDrag);
    return () => window.removeEventListener('dragend', endCardDrag);
  }, []);

  const handleCardDeleteZoneDragEnter = (e: React.DragEvent) => {
    if (!activeDrag || !canWrite) return;
    e.preventDefault();
    if (Array.from(e.dataTransfer.types).includes(SWIMLANE_DRAG_MIME)) return;
    setCardDeleteDropHover(true);
  };

  const handleCardDeleteZoneDragOver = (e: React.DragEvent) => {
    if (!activeDrag || !canWrite) return;
    if (Array.from(e.dataTransfer.types).includes(SWIMLANE_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setCardDeleteDropHover(true);
  };

  const handleCardDeleteZoneDragLeave = (e: React.DragEvent) => {
    const root = e.currentTarget as HTMLElement;
    const next = e.relatedTarget as Node | null;
    if (next && root.contains(next)) return;
    setCardDeleteDropHover(false);
  };

  const handleCardDeleteZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setCardDeleteDropHover(false);
    if (Array.from(e.dataTransfer.types).includes(SWIMLANE_DRAG_MIME)) {
      setActiveDrag(null);
      return;
    }
    const plain = e.dataTransfer.getData('text/plain');
    if (plain.startsWith('swimlane:')) {
      setActiveDrag(null);
      return;
    }
    const cardId = plain || e.dataTransfer.getData('application/x-kanban-card-id');
    if (!cardId || !boardData) {
      setActiveDrag(null);
      return;
    }
    const row = boardData.cards.find((c) => c.id === cardId);
    setActiveDrag(null);
    if (!row) return;
    setDeleteCardTarget({ id: cardId, title: row.title });
  };

  const confirmDeleteCard = async () => {
    if (!deleteCardTarget || !boardId) return;
    setDeleteCardLoading(true);
    try {
      await kanbanApiService.deleteCard(deleteCardTarget.id);
      if (selectedCardId === deleteCardTarget.id) {
        setSelectedCardId(null);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('card');
          return next;
        });
      }
      setDeleteCardTarget(null);
      const fresh = await kanbanApiService.getFullBoard(boardId);
      setBoardData(normalizeFullBoardArrays(fresh, boardId));
    } catch (err) {
      console.error('Failed to delete card:', err);
    } finally {
      setDeleteCardLoading(false);
    }
  };

  const handleListDragOver = (
    e: React.DragEvent,
    listId: string,
    swimlaneBucket: string | undefined,
    _orderedRowRootIds: string[],
  ) => {
    if (!activeDrag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const colKey = columnRefKey(listId, swimlaneBucket, useSwimlaneBuckets);
    const flatOrdered = listCardFlatOrderRef.current[colKey] ?? [];
    const bundle = new Set(activeDrag.movedCardIds ?? [activeDrag.cardId]);
    const root = e.currentTarget as HTMLElement;
    const insertAt = listInsertIndexFromPointer(e.clientY, root, flatOrdered, bundle);
    setActiveDrag((prev) =>
      prev && (prev.pointerColumnKey !== colKey || prev.insertIndex !== insertAt)
        ? { ...prev, pointerColumnKey: colKey, insertIndex: insertAt }
        : prev,
    );
  };

  const handleListDragLeave = (e: React.DragEvent, listId: string, swimlaneBucket: string | undefined) => {
    if (!activeDrag) return;
    const colKey = columnRefKey(listId, swimlaneBucket, useSwimlaneBuckets);
    const root = e.currentTarget as HTMLElement;
    const next = e.relatedTarget as Node | null;
    if (next && root.contains(next)) return;
    setActiveDrag((prev) => (prev && prev.pointerColumnKey === colKey ? { ...prev, pointerColumnKey: null, insertIndex: null } : prev));
  };

  const handleDropOnList = async (
    e: React.DragEvent,
    listId: string,
    swimlaneBucket: string | undefined,
    flatOrderedCardIds: string[],
  ) => {
    e.preventDefault();
    if (Array.from(e.dataTransfer.types).includes(SWIMLANE_DRAG_MIME)) {
      setActiveDrag(null);
      return;
    }
    const plain = e.dataTransfer.getData('text/plain');
    if (plain.startsWith('swimlane:')) {
      setActiveDrag(null);
      return;
    }
    const cardId = plain || e.dataTransfer.getData('application/x-kanban-card-id');
    if (!cardId || !boardData || !boardId) {
      setActiveDrag(null);
      return;
    }
    const movedBundle =
      activeDrag && activeDrag.cardId === cardId && activeDrag.movedCardIds?.length
        ? activeDrag.movedCardIds
        : [cardId];
    const bundleSet = new Set(movedBundle);
    const cardRow = boardData.cards.find((c) => c.id === cardId);

    const cardById = new Map(boardData.cards.map((c) => [c.id, c]));

    const root = e.currentTarget as HTMLElement;
    const insertAt = listInsertIndexFromPointer(e.clientY, root, flatOrderedCardIds, bundleSet);

    const flatWithoutBundle = flatOrderedCardIds.filter((id) => !bundleSet.has(id));

    const reparentSubtaskUnderId =
      cardRow?.card_type === 'subtask' && activeDrag?.dragMode === 'solo'
        ? resolveReparentNonSubtaskParent(flatWithoutBundle, insertAt, cardById)
        : null;

    const reassignSubtaskId =
      cardRow && cardRow.card_type !== 'subtask'
        ? findSubtaskToReassignBelowInsert(flatWithoutBundle, insertAt, cardId, cardById)
        : null;

    const positionById = new Map<string, number>();
    flatOrderedCardIds.forEach((id) => {
      const c = boardData.cards.find((x) => x.id === id);
      if (c) positionById.set(id, c.position);
    });
    const newPrimaryPosition = positionForInsert(flatOrderedCardIds, bundleSet, insertAt, positionById);
    const targetSwimlaneId =
      useSwimlaneBuckets && swimlaneBucket !== undefined
        ? swimlaneBucket === '__unsorted__'
          ? null
          : swimlaneBucket
        : undefined;
    const baseMovePayload: { list_id: string; swimlane_id?: string | null; position: number } = {
      list_id: listId,
      position: newPrimaryPosition,
    };
    if (useSwimlaneBuckets && swimlaneBucket !== undefined && cardRow) {
      const cur = cardRow.swimlane_id ?? null;
      const next = targetSwimlaneId ?? null;
      if (cur !== next) baseMovePayload.swimlane_id = next;
    }
    setActiveDrag(null);

    const actorId = identity?.user_uuid?.trim() ?? '';
    const optimisticSoloLink: KanbanCardLink | null =
      reparentSubtaskUnderId && cardRow?.card_type === 'subtask'
        ? {
            id: `optimistic-kanban-link-${cardId}`,
            card_id: reparentSubtaskUnderId,
            link_type: 'kanban_card',
            link_target_id: cardId,
            notes: null,
            linked_by: actorId || 'unknown',
            linked_at: Date.now(),
          }
        : null;
    const optimisticReassignLink: KanbanCardLink | null =
      reassignSubtaskId && cardRow && cardRow.card_type !== 'subtask'
        ? {
            id: `optimistic-kanban-link-${reassignSubtaskId}-to-${cardId}`,
            card_id: cardId,
            link_type: 'kanban_card',
            link_target_id: reassignSubtaskId,
            notes: null,
            linked_by: actorId || 'unknown',
            linked_at: Date.now(),
          }
        : null;

    setBoardData((prev) => {
      if (!prev) return prev;
      let nextBoard = prev;
      if (optimisticSoloLink && reparentSubtaskUnderId) {
        nextBoard = removeKanbanParentLinkToSubtask(nextBoard, cardId);
        nextBoard = mergeParentKanbanLinkOnCard(nextBoard, reparentSubtaskUnderId, cardId, optimisticSoloLink);
      }
      if (optimisticReassignLink && reassignSubtaskId) {
        nextBoard = removeKanbanParentLinkToSubtask(nextBoard, reassignSubtaskId);
        nextBoard = mergeParentKanbanLinkOnCard(nextBoard, cardId, reassignSubtaskId, optimisticReassignLink);
      }
      const updatedCards = nextBoard.cards.map((c) => {
        if (!bundleSet.has(c.id)) return c;
        const idx = movedBundle.indexOf(c.id);
        const pos = newPrimaryPosition + (idx + 1) * 1e-6;
        let next = { ...c, position: pos, list_id: listId };
        if ('swimlane_id' in baseMovePayload) next = { ...next, swimlane_id: baseMovePayload.swimlane_id ?? null };
        return next;
      });
      return { ...nextBoard, cards: updatedCards };
    });

    try {
      const results = await Promise.all(
        movedBundle.map((id, idx) =>
          kanbanApiService.moveCard(id, {
            ...baseMovePayload,
            position: newPrimaryPosition + (idx + 1) * 1e-6,
          }),
        ),
      );
      setBoardData((prev) => {
        if (!prev) return prev;
        const updates = movedBundle.map((id, i) => ({ cardId: id, api: results[i] }));
        return normalizeFullBoardArrays(applyMoveCardApiResponseMany(prev, updates), boardId);
      });

      if (reparentSubtaskUnderId && cardRow?.card_type === 'subtask') {
        try {
          const apiLink = await kanbanApiService.createCardLink(reparentSubtaskUnderId, {
            link_type: 'kanban_card',
            link_target_id: cardId,
          });
          setBoardData((p) => {
            if (!p) return p;
            const stripped = removeKanbanParentLinkToSubtask(p, cardId);
            return normalizeFullBoardArrays(
              mergeParentKanbanLinkOnCard(stripped, reparentSubtaskUnderId, cardId, apiLink),
              boardId,
            );
          });
        } catch (linkErr) {
          console.error('Failed to link subtask to parent card:', linkErr);
          try {
            const fresh = await kanbanApiService.getFullBoard(boardId);
            setBoardData(normalizeFullBoardArrays(fresh, boardId));
          } catch {
            /* ignore */
          }
        }
      } else if (reassignSubtaskId && cardRow && cardRow.card_type !== 'subtask') {
        try {
          const apiLink = await kanbanApiService.createCardLink(cardId, {
            link_type: 'kanban_card',
            link_target_id: reassignSubtaskId,
          });
          setBoardData((p) => {
            if (!p) return p;
            const stripped = removeKanbanParentLinkToSubtask(p, reassignSubtaskId);
            return normalizeFullBoardArrays(
              mergeParentKanbanLinkOnCard(stripped, cardId, reassignSubtaskId, apiLink),
              boardId,
            );
          });
        } catch (linkErr) {
          console.error('Failed to relink subtask to new parent:', linkErr);
          try {
            const fresh = await kanbanApiService.getFullBoard(boardId);
            setBoardData(normalizeFullBoardArrays(fresh, boardId));
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err) {
      console.error('Failed to move card:', err);
      const fresh = await kanbanApiService.getFullBoard(boardId);
      setBoardData(normalizeFullBoardArrays(fresh, boardId));
    }
  };

  const handleQuickCreateCard = async (listId: string, title: string) => {
    if (!boardId || !title.trim()) return;
    try {
      await kanbanApiService.createCard(boardId, { list_id: listId, title: title.trim() });
      const fresh = await kanbanApiService.getFullBoard(boardId);
      setBoardData(normalizeFullBoardArrays(fresh, boardId));
    } catch (err) {
      console.error('Failed to create card:', err);
    }
  };

  // ─── List Actions (Feature 4) ─────────────────────────────────────────────

  const handleMoveList = async (listId: string, newPosition: number) => {
    try {
      await kanbanApiService.moveList(listId, newPosition);
      void refreshBoard();
    } catch (e) {
      console.error('Failed to move list:', e);
    }
  };

  const handleArchiveList = async (listId: string) => {
    try {
      await kanbanApiService.archiveList(listId, true);
      void refreshBoard();
    } catch (e) {
      console.error('Failed to archive list:', e);
    }
  };

  const confirmDeleteList = async () => {
    if (!deleteListTarget) return;
    setDeleteListLoading(true);
    try {
      await kanbanApiService.deleteList(deleteListTarget);
      setDeleteListTarget(null);
      void refreshBoard();
    } catch (e) {
      console.error('Failed to delete list:', e);
    } finally {
      setDeleteListLoading(false);
    }
  };

  // ─── Archive Board (Feature 6) ────────────────────────────────────────────

  const handleArchiveBoard = async () => {
    if (!boardId) return;
    setArchiveBoardLoading(true);
    try {
      await kanbanApiService.archiveBoard(boardId, true);
      navigate('/workspaces');
    } catch (e) {
      console.error('Failed to archive board:', e);
    } finally {
      setArchiveBoardLoading(false);
    }
  };

  // ─── Swimlane Actions (Feature 7) ─────────────────────────────────────────

  const handleToggleSwimlaneCollapse = async (swimlaneId: string) => {
    const isCollapsed = collapsedSwimlanes.has(swimlaneId);
    setCollapsedSwimlanes((prev) => {
      const next = new Set(prev);
      if (isCollapsed) next.delete(swimlaneId); else next.add(swimlaneId);
      return next;
    });
    try {
      await kanbanApiService.collapseSwimlane(swimlaneId, !isCollapsed);
    } catch (e) {
      console.error(e);
    }
  };

  const mergeSwimlaneFromApi = useCallback((swimlaneId: string, api: KanbanSwimlane) => {
    setBoardData((prev) => {
      if (!prev) return prev;
      const swims = prev.swimlanes.map((s) => (s.id === swimlaneId ? { ...s, ...api } : s));
      return { ...prev, swimlanes: [...swims].sort((a, b) => a.position - b.position) };
    });
  }, []);

  const handleMoveSwimlane = async (swimlaneId: string, newPosition: number) => {
    try {
      const updated = await kanbanApiService.moveSwimlane(swimlaneId, newPosition);
      mergeSwimlaneFromApi(swimlaneId, updated);
    } catch (e) {
      console.error(e);
      void refreshBoard();
    }
  };

  const handleSwimlaneDragStart = useCallback((e: React.DragEvent, swimlaneId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(SWIMLANE_DRAG_MIME, swimlaneId);
    e.dataTransfer.setData('text/plain', `swimlane:${swimlaneId}`);
    setDraggedSwimlaneId(swimlaneId);
  }, []);

  const handleSwimlaneDragEnd = useCallback(() => {
    setDraggedSwimlaneId(null);
  }, []);

  const handleSwimlaneHeaderDragOver = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    if (!types.includes(SWIMLANE_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSwimlaneDropOnHeader = useCallback(
    async (e: React.DragEvent, targetSwimlaneId: string) => {
      e.preventDefault();
      e.stopPropagation();
      let dragId = e.dataTransfer.getData(SWIMLANE_DRAG_MIME);
      if (!dragId) {
        const plain = e.dataTransfer.getData('text/plain');
        if (plain.startsWith('swimlane:')) dragId = plain.slice('swimlane:'.length);
      }
      setDraggedSwimlaneId(null);
      if (!dragId || dragId === targetSwimlaneId || !boardData) return;

      const sorted = [...boardData.swimlanes].sort((a, b) => a.position - b.position);
      const root = e.currentTarget as HTMLElement;
      const r = root.getBoundingClientRect();
      const dropAfter = e.clientY > r.top + r.height / 2;
      const newPos = computeSwimlaneReorderPosition(sorted, dragId, targetSwimlaneId, dropAfter);
      if (newPos == null) return;

      const ids = sorted.map((s) => s.id);
      const without = ids.filter((id) => id !== dragId);
      let insertAt = without.indexOf(targetSwimlaneId);
      if (insertAt < 0) return;
      if (dropAfter) insertAt += 1;
      const newIds = [...without.slice(0, insertAt), dragId, ...without.slice(insertAt)];
      setBoardData((prev) => {
        if (!prev) return prev;
        const newSwims = newIds
          .map((id, i) => {
            const row = prev.swimlanes.find((s) => s.id === id);
            return row ? { ...row, position: i } : null;
          })
          .filter((s): s is KanbanSwimlane => s != null);
        return { ...prev, swimlanes: newSwims };
      });

      try {
        const updated = await kanbanApiService.moveSwimlane(dragId, newPos);
        mergeSwimlaneFromApi(dragId, updated);
      } catch (err) {
        console.error('Failed to reorder swimlane:', err);
        void refreshBoard();
      }
    },
    [boardData, refreshBoard, mergeSwimlaneFromApi],
  );

  const openEditSwimlane = (sw: KanbanSwimlane) => {
    setEditSwimlaneTarget(sw);
    setEditSwimlaneTitleVal(sw.title);
    setEditSwimlaneColorVal(sw.color ?? '');
  };

  const submitEditSwimlane = async () => {
    if (!editSwimlaneTarget) return;
    setEditSwimlaneSubmitting(true);
    try {
      // Fetch fresh swimlane data before update
      await kanbanApiService.getSwimlane(editSwimlaneTarget.id);
      await kanbanApiService.updateSwimlane(editSwimlaneTarget.id, {
        title: editSwimlaneTitleVal.trim() || editSwimlaneTarget.title,
        color: editSwimlaneColorVal.trim() || null,
      });
      setEditSwimlaneTarget(null);
      void refreshBoard();
    } catch (e) {
      console.error(e);
    } finally {
      setEditSwimlaneSubmitting(false);
    }
  };

  const confirmDeleteSwimlane = async () => {
    if (!deleteSwimlaneTarget) return;
    setDeleteSwimlaneLoading(true);
    try {
      await kanbanApiService.deleteSwimlane(deleteSwimlaneTarget);
      setDeleteSwimlaneTarget(null);
      void refreshBoard();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteSwimlaneLoading(false);
    }
  };

  // ─── Cadence Actions (Feature 10) ─────────────────────────────────────────

  const confirmDeleteCadence = async () => {
    if (!deleteCadenceTarget) return;
    setDeleteCadenceLoading(true);
    try {
      await kanbanApiService.deleteCadence(deleteCadenceTarget);
      setDeleteCadenceTarget(null);
      void loadCadences();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteCadenceLoading(false);
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderListColumn = (
    list: KanbanList,
    listIndex: number,
    cards: KanbanCardSummary[],
    swimlaneBucket?: string,
  ) => {
    const colKey = columnRefKey(list.id, swimlaneBucket, useSwimlaneBuckets);
    const descendantMap = buildDescendantIdsByCardId(cards, swimlaneBucket);
    const stackRows = buildBoardCardStackRows(cards, descendantMap);
    const flatOrdered = flattenStackRowsToOrderedCardIds(stackRows);
    listCardFlatOrderRef.current[colKey] = flatOrdered;

    const pointerMatches =
      activeDrag &&
      activeDrag.pointerColumnKey === colKey &&
      activeDrag.insertIndex !== null &&
      activeDrag.insertIndex !== undefined;
    const insertIdxFlat = pointerMatches ? activeDrag!.insertIndex! : null;

    const movedSet =
      activeDrag?.movedCardIds && activeDrag.movedCardIds.length > 0
        ? new Set(activeDrag.movedCardIds)
        : activeDrag?.cardId
          ? new Set([activeDrag.cardId])
          : null;

    const withoutDragRows = movedSet ? stackRowsWithoutMovedCards(stackRows, movedSet) : stackRows;
    let displayRows = withoutDragRows;
    if (insertIdxFlat != null && activeDrag) {
      const draggedRow = stackRowForDraggedCardId(stackRows, activeDrag.cardId);
      if (draggedRow) {
        displayRows = stackRowsWithGhostAtFlatIndex(withoutDragRows, draggedRow, insertIdxFlat);
      }
    }

    const dropIndicator = (
      <motion.div
        layout
        initial={false}
        animate={{ opacity: 1, scaleY: 1 }}
        transition={{ type: 'spring', stiffness: 520, damping: 38 }}
        className="h-1 rounded-full bg-accent-400/90 mx-0.5"
        aria-hidden
      />
    );

    const flatLenRowsPrefix = (rows: typeof stackRows, endExclusive: number) => {
      let s = 0;
      for (let j = 0; j < endExclusive; j++) {
        s += stackRowCardIds(rows[j]).length;
      }
      return s;
    };

    const flatDisp = flattenStackRowsToOrderedCardIds(displayRows);
    const ghostIdx =
      pointerMatches && activeDrag && insertIdxFlat != null
        ? flatDisp.indexOf(activeDrag.cardId)
        : -1;
    const flatWithoutDraggedForSlot = flatOrdered.filter((id) => !movedSet?.has(id));

    return (
      <div
        key={swimlaneBucket !== undefined ? `${list.id}:${swimlaneBucket}` : list.id}
        className={cn(
          'flex-shrink-0 w-[300px] md:w-[320px] flex flex-col max-h-full',
          isMobile && 'snap-start',
        )}
      >
        <div className="subcard rounded-b-none border-b-0 px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="eyebrow text-white truncate">{list.title}</h3>
            <KanbanWIPIndicator count={cards.length} limit={list.wip_limit} />
            <KanbanListRoleBadge role={list.list_role} />
          </div>
          <div className="flex items-center gap-1">
            {canManage && (
              <ListHeaderMenu
                listIndex={listIndex}
                totalLists={sortedLists.length}
                onEdit={() => setEditListTarget(list)}
                onMoveLeft={() => {
                  if (listIndex > 0) {
                    const targetPos = sortedLists[listIndex - 1].position - 0.5;
                    void handleMoveList(list.id, targetPos);
                  }
                }}
                onMoveRight={() => {
                  if (listIndex < sortedLists.length - 1) {
                    const targetPos = sortedLists[listIndex + 1].position + 0.5;
                    void handleMoveList(list.id, targetPos);
                  }
                }}
                onArchive={() => void handleArchiveList(list.id)}
                onDelete={() => setDeleteListTarget(list.id)}
                onBatchMoveTo={(targetListId) => {
                  const cardIds = cards.map((c) => c.id);
                  void handleBatchMove(cardIds, targetListId);
                }}
                otherLists={sortedLists.filter((l) => l.id !== list.id).map((l) => ({ id: l.id, title: l.title }))}
              />
            )}
          </div>
        </div>

        <div
          className={cn(
            'flex-1 overflow-y-auto p-2 space-y-2 subcard rounded-t-none border-t-0 min-h-[100px]',
            activeDrag && 'ring-1 ring-inset ring-accent-400/25',
          )}
          onDragOver={(e) => handleListDragOver(e, list.id, swimlaneBucket, flatOrdered)}
          onDragLeave={(e) => handleListDragLeave(e, list.id, swimlaneBucket)}
          onDrop={(e) => void handleDropOnList(e, list.id, swimlaneBucket, flatOrdered)}
        >
          {displayRows.length === 0 && (
            <div className="text-center py-6 text-xs text-glass-500 rounded-lg border border-dashed border-glass-200/15">Drop cards here</div>
          )}
          {ghostIdx === 0 ? dropIndicator : null}
          {displayRows.map((row, i) => {
            const swimlaneBadgeFor = (card: KanbanCardSummary) =>
              hasSwimlanes && !useSwimlaneBuckets
                ? card.swimlane_id
                  ? {
                      title: swimlaneTitleById.get(card.swimlane_id) ?? 'Swimlane',
                      color: swimlaneColorById.get(card.swimlane_id) ?? null,
                    }
                  : { title: 'Unsorted', color: null }
                : undefined;

            if (row.kind === 'stack') {
              const isDraggingStack =
                activeDrag?.dragMode !== 'solo' && activeDrag?.cardId === row.root.id;
              const rootDragFrozen =
                activeDrag?.dragMode === 'solo' &&
                activeDrag.cardId !== row.root.id &&
                row.nested.some((s) => s.id === activeDrag.cardId);
              const flatPrefix = flatLenRowsPrefix(displayRows, i);
              const intraSlot =
                insertIdxFlat != null
                  ? intraStackDropSlot(row, flatWithoutDraggedForSlot, insertIdxFlat)
                  : null;
              return (
                <Fragment key={row.root.id}>
                  {ghostIdx === flatPrefix ? dropIndicator : null}
                  <motion.div
                    layout
                    transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.85 }}
                  >
                    <KanbanStackedCards
                      root={row.root}
                      nested={row.nested}
                      onClick={(id) => openCardDetail(id)}
                      wipWarningHours={wipWarningHours}
                      swimlaneBadge={swimlaneBadgeFor(row.root)}
                      draggable={canWrite}
                      nestedDraggable={canWrite}
                      rootDragFrozen={rootDragFrozen}
                      onDragStart={handleCardDragStart}
                      onDragEnd={handleCardDragEnd}
                      isDraggingStack={isDraggingStack}
                      intraStackDropSlot={intraSlot}
                      intraStackDropIndicator={dropIndicator}
                    />
                  </motion.div>
                </Fragment>
              );
            }

            const card = row.card;
            const isDraggingThis = activeDrag?.cardId === card.id;
            const flatPrefix = flatLenRowsPrefix(displayRows, i);
            return (
              <Fragment key={card.id}>
                {ghostIdx === flatPrefix ? dropIndicator : null}
                <motion.div
                  layout
                  transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.85 }}
                  className={cn(isDraggingThis && 'opacity-[0.42] scale-[0.985]')}
                >
                  <KanbanCardMini
                    card={card}
                    onClick={(id) => openCardDetail(id)}
                    wipWarningHours={wipWarningHours}
                    swimlaneBadge={swimlaneBadgeFor(card)}
                    draggable={canWrite}
                    onDragStart={handleCardDragStart}
                    onDragEnd={handleCardDragEnd}
                  />
                </motion.div>
              </Fragment>
            );
          })}
          {ghostIdx === flatDisp.length ? dropIndicator : null}
          {canWrite && <QuickAddCard listId={list.id} onAdd={handleQuickCreateCard} />}
        </div>
      </div>
    );
  };

  const renderFlatBoard = () => (
    <LayoutGroup id={boardId ? `kanban-flat-${boardId}` : 'kanban-flat'}>
      <div
        className={cn(
          'flex gap-4 overflow-x-auto flex-1 pb-4 scrollbar-hide',
          isMobile && 'snap-x snap-mandatory',
        )}
      >
        {sortedLists.map((list, idx) => {
          const listCards = cardsByList[list.id] || [];
          return renderListColumn(list, idx, listCards);
        })}
        {canManage && (
          <div className="flex-shrink-0 w-[300px] md:w-[320px] flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setShowCreateList(true)}
              className="subcard border-dashed p-4 w-full text-center text-glass-400 hover:text-white hover:border-glass-200/25 transition-colors"
            >
              <Plus className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm">Add List</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCreateSwimlane(true)}
              className="subcard border-dashed p-3 w-full text-center text-glass-400 hover:text-white hover:border-glass-200/25 transition-colors text-sm"
            >
              <Layers className="w-4 h-4 inline mr-1" />
              Add Swimlane
            </button>
          </div>
        )}
      </div>
    </LayoutGroup>
  );

  const renderSwimlaneBoard = () => {
    if (!cardsByListAndSwimlane) return null;

    const swimlaneCardCount = (swimlaneKey: string) =>
      sortedLists.reduce((sum, l) => sum + (cardsByListAndSwimlane[l.id]?.[swimlaneKey]?.length ?? 0), 0);

    return (
      <div className="flex-1 overflow-y-auto space-y-2 pb-4">
        {sortedSwimlanes.map((sw, swIdx) => {
          const isCollapsed = collapsedSwimlanes.has(sw.id);
          return (
            <div key={sw.id}>
              <SwimlaneHeader
                swimlane={sw}
                cardCount={swimlaneCardCount(sw.id)}
                isCollapsed={isCollapsed}
                canWrite={canWrite}
                canManage={canManage}
                swimlaneIndex={swIdx}
                totalSwimlanes={sortedSwimlanes.length}
                onToggleCollapse={() => void handleToggleSwimlaneCollapse(sw.id)}
                onEdit={() => openEditSwimlane(sw)}
                onDelete={() => setDeleteSwimlaneTarget(sw.id)}
                onMoveUp={() => {
                  if (swIdx > 0) {
                    const targetPos = sortedSwimlanes[swIdx - 1].position - 0.5;
                    void handleMoveSwimlane(sw.id, targetPos);
                  }
                }}
                onMoveDown={() => {
                  if (swIdx < sortedSwimlanes.length - 1) {
                    const targetPos = sortedSwimlanes[swIdx + 1].position + 0.5;
                    void handleMoveSwimlane(sw.id, targetPos);
                  }
                }}
                swimlaneDragActive={draggedSwimlaneId !== null}
                onSwimlaneDragStart={(e) => handleSwimlaneDragStart(e, sw.id)}
                onSwimlaneDragEnd={handleSwimlaneDragEnd}
                onSwimlaneDragOver={handleSwimlaneHeaderDragOver}
                onSwimlaneDragLeave={() => {}}
                onSwimlaneDrop={(e) => void handleSwimlaneDropOnHeader(e, sw.id)}
              />
              {!isCollapsed && (
                <LayoutGroup id={boardId ? `kanban-sw-${boardId}-${sw.id}` : `kanban-sw-${sw.id}`}>
                  <div
                    className={cn(
                      'flex gap-4 overflow-x-auto pb-2 scrollbar-hide',
                      isMobile && 'snap-x snap-mandatory',
                    )}
                  >
                    {sortedLists.map((list, idx) => {
                      const cards = cardsByListAndSwimlane[list.id]?.[sw.id] ?? [];
                      return renderListColumn(list, idx, cards, sw.id);
                    })}
                  </div>
                </LayoutGroup>
              )}
            </div>
          );
        })}

        {/* Unsorted row */}
        <div>
          <div className="flex items-center gap-2 py-1.5 px-2.5 glass-card mb-1.5">
            <div className="w-1 h-6 rounded-full flex-shrink-0 bg-glass-500" />
            <h3 className="text-sm font-semibold text-glass-400 flex-1">Unsorted</h3>
            <span className="badge badge-neutral">{swimlaneCardCount('__unsorted__')} cards</span>
          </div>
          <LayoutGroup id={boardId ? `kanban-sw-${boardId}-unsorted` : 'kanban-sw-unsorted'}>
            <div
              className={cn(
                'flex gap-4 overflow-x-auto pb-2 scrollbar-hide',
                isMobile && 'snap-x snap-mandatory',
              )}
            >
              {sortedLists.map((list, idx) => {
                const cards = cardsByListAndSwimlane[list.id]?.['__unsorted__'] ?? [];
                return renderListColumn(list, idx, cards, '__unsorted__');
              })}
            </div>
          </LayoutGroup>
        </div>

        {canWrite && (
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setShowCreateList(true)}
              className="subcard border-dashed p-3 text-center text-glass-400 hover:text-white hover:border-glass-200/25 transition-colors text-sm"
            >
              <Plus className="w-4 h-4 inline mr-1" /> Add List
            </button>
            <button
              type="button"
              onClick={() => setShowCreateSwimlane(true)}
              className="subcard border-dashed p-3 text-center text-glass-400 hover:text-white hover:border-glass-200/25 transition-colors text-sm"
            >
              <Layers className="w-4 h-4 inline mr-1" /> Add Swimlane
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] md:h-[calc(100vh-4rem)] relative">
      {syncSummary && (
        <div
          className="fixed top-20 left-1/2 z-[10000] -translate-x-1/2 max-w-[min(90vw,28rem)] px-4 py-2 glass-card border-accent-500/30 text-sm text-white text-center pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <span className="text-accent-200 font-medium">Updated</span>
          <span className="text-glass-200"> — {syncSummary}</span>
        </div>
      )}
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 mb-4">
        <PageHeader
          className="gap-3"
          backTo="/workspaces"
          backLabel="Workspaces"
          eyebrow="Planning"
          title={boardData?.board?.title || 'Loading…'}
          titleAdornment={
            <>
              {boardData?.board?.team && (
                <span className="badge badge-accent">{boardData.board.team}</span>
              )}
              {canRead && !canWrite && (
                <span className="badge badge-neutral">Read Only</span>
              )}
            </>
          }
          description={boardData?.board?.description || undefined}
          actions={
            <>
              {canWrite && (
                <button type="button" onClick={() => setShowCreateCard(true)} className="btn-primary" title="New Card (N)">
                  <Plus className="w-4 h-4" /><span className="hidden sm:inline">Card</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowFilters((f) => !f)}
                className={cn('btn-icon', showFilters && 'bg-accent-500/15 text-accent-300')}
                title="Filter (F)"
                aria-label="Toggle filters"
                aria-pressed={showFilters}
              >
                <Filter className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => void refreshBoard()}
                className={cn('btn-icon', backgroundRefreshing && 'opacity-80')}
                title="Refresh (R)"
                aria-label="Refresh board"
              >
                <RefreshCw className={cn('w-4 h-4', backgroundRefreshing && 'animate-spin')} />
              </button>

              {/* Power-user overflow menu */}
              <BoardOverflowMenu
                onMetrics={() => setShowMetrics(true)}
                onLabels={canManage ? () => setShowLabels(true) : undefined}
                onCadences={canManage ? () => setShowCadencesPanel(true) : undefined}
                onSettings={canManage ? () => setShowSettings(true) : undefined}
                onAccess={canManageAccess ? () => setShowBoardAccess(true) : undefined}
                onActivity={() => setShowActivity(true)}
                onStats={() => setShowBoardStats(v => !v)}
                onLinkedEvents={canSeeLinkedEvents ? () => setShowLinkedEvents(true) : undefined}
                onEdit={canManage ? () => setShowEditBoard(true) : undefined}
                onArchive={canManage ? () => setShowArchiveBoard(true) : undefined}
                onDelete={canManage ? () => setShowDeleteBoard(true) : undefined}
                showBoardStats={showBoardStats}
              />
            </>
          }
        >
          <div className="flex flex-col gap-3">
            {/* ── Board-Level Quick Views (Feature 5) ─────────────────────────── */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => openViewPanel('my_cards')}
                className={cn('glass-button-sm', viewPanel === 'my_cards' && 'bg-accent-500/15 border-accent-500/30 text-accent-200')}
                aria-pressed={viewPanel === 'my_cards'}
              >
                <User className="w-3.5 h-3.5" /> My Cards
              </button>
              <button
                type="button"
                onClick={() => openViewPanel('overdue')}
                className={cn('glass-button-sm', viewPanel === 'overdue' && 'bg-accent-500/15 border-accent-500/30 text-accent-200')}
                aria-pressed={viewPanel === 'overdue'}
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Overdue
              </button>
              <button
                type="button"
                onClick={() => openViewPanel('search')}
                className={cn('glass-button-sm', viewPanel === 'search' && 'bg-accent-500/15 border-accent-500/30 text-accent-200')}
                aria-pressed={viewPanel === 'search'}
              >
                <Search className="w-3.5 h-3.5" /> Search Cards
              </button>
              <button
                type="button"
                onClick={() => openViewPanel('due_range')}
                className={cn('glass-button-sm', viewPanel === 'due_range' && 'bg-accent-500/15 border-accent-500/30 text-accent-200')}
                aria-pressed={viewPanel === 'due_range'}
              >
                <Clock className="w-3.5 h-3.5" /> Due This Week
              </button>
              {hasSwimlanes && (
                <div className="segmented" role="group" aria-label="Board layout">
                  <button
                    type="button"
                    onClick={() => setBoardColumnLayout('columns')}
                    className="segmented-item segmented-item-sm"
                    data-active={boardColumnLayout === 'columns' ? 'true' : 'false'}
                    title="Classic columns (default). Cards stay in their swimlane; drag moves list only."
                    aria-pressed={boardColumnLayout === 'columns'}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Columns</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBoardColumnLayout('swimlanes')}
                    className="segmented-item segmented-item-sm"
                    data-active={boardColumnLayout === 'swimlanes' ? 'true' : 'false'}
                    title="Swimlanes: horizontal rows per lane. Drag cards between lanes here."
                    aria-pressed={boardColumnLayout === 'swimlanes'}
                  >
                    <Rows3 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Swimlanes</span>
                  </button>
                </div>
              )}
              {canManage && (
                <button
                  type="button"
                  onClick={() => setShowCreateSwimlane(true)}
                  className="glass-button-sm"
                >
                  <Layers className="w-3.5 h-3.5" /> Add Swimlane
                </button>
              )}
            </div>

            {/* ── Board Stats (Feature 8) ─────────────────────────────────────── */}
            {showBoardStats && (
              <div className="glass-card p-3 md:p-4">
                {boardStatsLoading ? (
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-3" aria-busy="true">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="space-y-1.5">
                        <SkeletonLoader className="h-3 w-14" />
                        <SkeletonLoader className="h-6 w-10" />
                      </div>
                    ))}
                  </div>
                ) : boardStats ? (
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                    {[
                      { label: 'Total Cards', value: boardStats.total_cards, tone: 'text-white' },
                      { label: 'Blocked', value: boardStats.cards_blocked, tone: 'text-red-400' },
                      { label: 'Overdue', value: boardStats.cards_overdue, tone: 'text-amber-400' },
                      { label: 'Completed', value: boardStats.cards_completed, tone: 'text-emerald-400' },
                      { label: 'Archived', value: boardStats.total_archived_cards, tone: 'text-glass-300' },
                      { label: 'Members', value: boardStats.active_members, tone: 'text-accent-300' },
                      { label: 'Comments', value: boardStats.total_comments, tone: 'text-glass-300' },
                      { label: 'Attachments', value: boardStats.total_attachments, tone: 'text-glass-300' },
                    ].map((s) => (
                      <div key={s.label} className="min-w-0">
                        <div className="eyebrow truncate">{s.label}</div>
                        <div className={cn('text-lg font-semibold tracking-tight tabular-nums', s.tone)}>{s.value.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-glass-500">No stats available</p>
                )}
                {/* Card count per list breakdown */}
                {cardCountByList.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-glass-200/10">
                    <h4 className="eyebrow mb-2">Cards per List</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {cardCountByList.map(c => (
                        <span key={c.list_id} className="chip">
                          {c.list_title}
                          <span className="text-white font-semibold tabular-nums">{c.card_count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Filters ─────────────────────────────────────────────────────── */}
            {showFilters && (
              <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                  <input
                    className="glass-input w-full pl-9"
                    placeholder="Search cards…"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
                <select
                  className="glass-select md:w-40"
                  aria-label="Filter by card type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as KanbanCardType | '')}
                >
                  <option value="">All Types</option>
                  {KANBAN_CARD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                <select
                  className="glass-select md:w-40"
                  aria-label="Filter by class of service"
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value as KanbanClassOfService | '')}
                >
                  <option value="">All Classes</option>
                  {KANBAN_CLASSES_OF_SERVICE.map((c) => (
                    <option key={c} value={c}>
                      {c.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-xs text-glass-400 cursor-pointer whitespace-nowrap md:px-1">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={showBlockedOnly}
                    onChange={(e) => setShowBlockedOnly(e.target.checked)}
                  />
                  Blocked
                </label>
                {(searchFilter || typeFilter || classFilter || showBlockedOnly) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchFilter('');
                      setTypeFilter('');
                      setClassFilter('');
                      setShowBlockedOnly(false);
                    }}
                    className="glass-button-secondary btn-xs"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        </PageHeader>
      </div>

      {/* ── Board Content ──────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto flex-1 pb-4" aria-busy="true">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="w-[300px] md:w-[320px] flex-shrink-0 flex flex-col">
              <div className="subcard rounded-b-none border-b-0 px-3 py-2.5 flex items-center justify-between">
                <SkeletonLoader className="h-3 w-24" />
                <SkeletonLoader className="h-5 w-8 rounded-full" />
              </div>
              <div className="subcard rounded-t-none border-t-0 p-2 space-y-2">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="glass-card p-3 space-y-2">
                    <SkeletonLoader className="h-4 w-3/4" />
                    <SkeletonLoader className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to Load Board"
          description={error}
          action={
            <button type="button" onClick={() => void loadBoard({ mode: 'initial' })} className="glass-button">
              Retry
            </button>
          }
        />
      ) : useSwimlaneBuckets ? (
        renderSwimlaneBoard()
      ) : (
        renderFlatBoard()
      )}

      {/* Drag-to-delete: only while a card drag is in progress (not tied to activeDrag preview state) */}
      {canWrite && !isLoading && !error && boardData && isDraggingCard && (
        <div
          className="fixed bottom-4 left-1/2 z-[9990] -translate-x-1/2 pointer-events-auto"
          onDragEnter={handleCardDeleteZoneDragEnter}
          onDragOver={handleCardDeleteZoneDragOver}
          onDragLeave={handleCardDeleteZoneDragLeave}
          onDrop={handleCardDeleteZoneDrop}
        >
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full border-2 transition-all duration-200',
              'border-glass-200/20 bg-[var(--bg-elevated)] backdrop-blur-md shadow-xl shadow-black/40',
              cardDeleteDropHover && 'scale-125 border-red-500/80 bg-red-500/15',
            )}
            title="Drop a card here to delete it"
            aria-label="Drop zone: delete card"
          >
            <Trash2
              className={cn(
                'h-6 w-6 transition-colors',
                cardDeleteDropHover ? 'text-red-300' : 'text-glass-400',
              )}
            />
          </div>
          <p className="mt-1.5 text-center text-[10px] text-glass-400 whitespace-nowrap max-w-[min(90vw,16rem)]">
            Release to delete card
          </p>
        </div>
      )}

      {/* ── Card Detail Panel ──────────────────────────────────────────────── */}
      {selectedCardId && (
        <KanbanCardDetailPanel
          cardId={selectedCardId}
          boardId={boardId!}
          boardData={boardData}
          onClose={closeCardDetail}
          onUpdated={() => void refreshBoard()}
          onCardHydrated={syncBoardCardFromFull}
          canWrite={canWrite}
          currentUserUuid={identity?.user_uuid}
          wsRefetchNonce={wsDetailRefetchNonce}
          cardIncidentsRefreshNonce={wsCardIncidentsNonce}
          cardEventsRefreshNonce={wsCardEventsNonce}
        />
      )}

      {/* ── Activity Sidebar (Feature 2) ──────────────────────────────────── */}
      {showActivity && (
        <div className="fixed inset-0 z-[9998] flex">
          <div className="flex-1 bg-[var(--bg-overlay)] backdrop-blur-sm" onClick={() => setShowActivity(false)} aria-hidden />
          <div className={cn(
            'w-full border-l border-glass-200/20 overflow-y-auto p-6 space-y-4 shadow-2xl shadow-black/40',
            'bg-[var(--bg-elevated)] backdrop-blur-xl',
            isMobile ? 'max-w-full' : 'max-w-md',
          )}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold tracking-tight text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-accent-400" /> Board Activity
              </h2>
              <button type="button" onClick={() => setShowActivity(false)} className="btn-icon" aria-label="Close activity" title="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {activityLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => <SkeletonLoader key={i} className="h-16 w-full" />)}
              </div>
            ) : activityEntries.length === 0 ? (
              <p className="text-sm text-glass-500">No activity recorded yet.</p>
            ) : (
              <ul className="space-y-3">
                {activityEntries.map((entry) => (
                  <li key={entry.id} className="subcard p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="badge badge-accent">{entry.action}</span>
                      <span className="text-xs text-glass-500 tabular-nums">
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-glass-200">{entry.description}</p>
                    <div className="text-xs text-glass-400 flex items-center gap-2">
                      <PlayerProfile uuid={entry.actor_id} inline showUuid={false} />
                      <button type="button" onClick={() => void loadActivityByActor(entry.actor_id)}
                        className="btn-icon btn-icon-sm text-accent-400 hover:text-accent-300" title="Filter by this actor" aria-label="Filter by this actor">
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    {(entry.old_value || entry.new_value) && (
                      <div className="text-xs text-glass-500 flex gap-3 mt-1">
                        {entry.old_value && <span className="line-through text-red-400/70">{entry.old_value}</span>}
                        {entry.new_value && <span className="text-emerald-400/70">{entry.new_value}</span>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── View Panel Overlay (Feature 5) ────────────────────────────────── */}
      {viewPanel && (
        <div className="fixed inset-0 z-[9997] flex">
          <div className="flex-1 bg-[var(--bg-overlay)] backdrop-blur-sm" onClick={() => setViewPanel(null)} aria-hidden />
          <div className={cn(
            'w-full border-l border-glass-200/20 overflow-y-auto p-6 space-y-4 shadow-2xl shadow-black/40',
            'bg-[var(--bg-elevated)] backdrop-blur-xl',
            isMobile ? 'max-w-full' : 'max-w-lg',
          )}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold tracking-tight text-white">
                {viewPanel === 'my_cards' && 'My Cards'}
                {viewPanel === 'overdue' && 'Overdue Cards'}
                {viewPanel === 'search' && 'Search Cards'}
                {viewPanel === 'due_range' && 'Cards Due This Week'}
                {viewPanel === 'by_actor' && 'Activity by Actor'}
              </h2>
              <button type="button" onClick={() => setViewPanel(null)} className="btn-icon" aria-label="Close panel" title="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {viewPanel === 'search' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  className="glass-input w-full pl-9"
                  placeholder="Search all cards…"
                  value={viewSearchQuery}
                  onChange={(e) => {
                    setViewSearchQuery(e.target.value);
                    void handleSearchCards(e.target.value);
                  }}
                />
              </div>
            )}

            {viewPanelLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <SkeletonLoader key={i} className="h-16 w-full" />)}
              </div>
            ) : viewPanelCards.length === 0 ? (
              <p className="text-sm text-glass-500">No cards found.</p>
            ) : (
              <ul className="space-y-2">
                {viewPanelCards.map((card) => (
                  <li
                    key={card.id}
                    className="subcard p-3 cursor-pointer hover:border-glass-200/25 transition-colors"
                    onClick={() => { openCardDetail(card.id); setViewPanel(null); }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <KanbanCardTypeBadge type={card.card_type} />
                      <KanbanPriorityBadge priority={card.priority} />
                      {card.is_blocked === 1 && (
                        <span className="badge badge-danger">Blocked</span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium text-white">{card.title}</h4>
                    {card.due_date && (
                      <div className={cn(
                        'text-xs mt-1 flex items-center gap-1',
                        card.due_date < Date.now() && !card.completed_at ? 'text-red-400' : 'text-glass-400',
                      )}>
                        <Clock className="w-3 h-3" />
                        Due {formatDistanceToNow(new Date(card.due_date), { addSuffix: true })}
                      </div>
                    )}
                    <KanbanClassOfServiceIndicator classOfService={card.class_of_service} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Cadences Panel (Feature 9 + 10) ──────────────────────────────── */}
      {showCadencesPanel && (
        <div className="fixed inset-0 z-[9997] flex">
          <div className="flex-1 bg-[var(--bg-overlay)] backdrop-blur-sm" onClick={() => setShowCadencesPanel(false)} aria-hidden />
          <div className={cn(
            'w-full border-l border-glass-200/20 overflow-y-auto p-6 space-y-4 shadow-2xl shadow-black/40',
            'bg-[var(--bg-elevated)] backdrop-blur-xl',
            isMobile ? 'max-w-full' : 'max-w-md',
          )}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold tracking-tight text-white flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-accent-400" /> Cadences
              </h2>
              <div className="flex items-center gap-2">
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => setShowCadenceCreate(true)}
                    className="glass-button-sm"
                  >
                    <Plus className="w-3.5 h-3.5" /> New
                  </button>
                )}
                <button type="button" onClick={() => setShowCadencesPanel(false)} className="btn-icon" aria-label="Close cadences" title="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {cadencesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <SkeletonLoader key={i} className="h-16 w-full" />)}
              </div>
            ) : cadencesList.length === 0 ? (
              <p className="text-sm text-glass-500">No cadences configured.</p>
            ) : (
              <ul className="space-y-2">
                {cadencesList.map((cad) => {
                  const cfg = CADENCE_TYPE_CONFIG[cad.cadence_type];
                  return (
                    <li key={cad.id} className="subcard p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="chip border-transparent"
                            style={{ backgroundColor: `${cfg.color}22`, color: cfg.color }}
                          >
                            {cfg.label}
                          </span>
                          <h4 className="text-sm font-medium text-white truncate">{cad.title}</h4>
                          {cad.is_active === 0 && (
                            <span className="text-xs text-glass-500">(inactive)</span>
                          )}
                        </div>
                        {canWrite && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => setEditCadenceTarget(cad)}
                              className="btn-icon btn-icon-sm"
                              title="Edit"
                              aria-label="Edit cadence"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteCadenceTarget(cad.id)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              title="Delete"
                              aria-label="Delete cadence"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-glass-400 flex flex-wrap gap-3 tabular-nums">
                        <span>Frequency: {cad.frequency}</span>
                        {cad.next_occurrence && (
                          <span>Next: {format(new Date(cad.next_occurrence), 'PPp')}</span>
                        )}
                        {cad.interval_days && (
                          <span>Every {cad.interval_days}d</span>
                        )}
                      </div>
                      {cad.description && (
                        <p className="text-xs text-glass-500">{cad.description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {showBoardAccess && canManageAccess && boardData?.board && (
        <BoardAccessModal
          boardId={boardData.board.id}
          boardTitle={boardData.board.title}
          onClose={() => setShowBoardAccess(false)}
          onSaved={() => void refreshBoard()}
        />
      )}

      {showEditBoard && boardData?.board && (
        <EditBoardModal
          isOpen={showEditBoard}
          onClose={() => setShowEditBoard(false)}
          board={boardData.board}
          onUpdated={() => { setShowEditBoard(false); void refreshBoard(); }}
        />
      )}

      {showCreateCard && boardData?.board && boardId && (
        <CreateCardModal
          isOpen={showCreateCard}
          onClose={() => setShowCreateCard(false)}
          boardId={boardId}
          lists={boardData.lists}
          swimlanes={boardData.swimlanes}
          labels={boardData.labels}
          onCreated={() => { setShowCreateCard(false); void refreshBoard(); }}
        />
      )}

      {showCreateList && boardId && (
        <CreateListModal
          isOpen={showCreateList}
          onClose={() => setShowCreateList(false)}
          boardId={boardId}
          onCreated={() => { setShowCreateList(false); void refreshBoard(); }}
        />
      )}

      {editListTarget && (
        <EditListModal
          isOpen={!!editListTarget}
          onClose={() => setEditListTarget(null)}
          list={editListTarget}
          onUpdated={() => { setEditListTarget(null); void refreshBoard(); }}
        />
      )}

      {showCreateSwimlane && boardId && (
        <CreateSwimlaneModal
          isOpen={showCreateSwimlane}
          onClose={() => setShowCreateSwimlane(false)}
          boardId={boardId}
          onCreated={() => { setShowCreateSwimlane(false); void refreshBoard(); }}
        />
      )}

      {showLabels && boardData?.board && boardId && (
        <KanbanLabelManagementModal
          isOpen={showLabels}
          onClose={() => setShowLabels(false)}
          boardId={boardId}
          labels={boardData.labels}
          onUpdated={() => void refreshBoard()}
        />
      )}

      {/* Cadence create modal (Feature 10) */}
      {showCadenceCreate && boardId && (
        <KanbanCadenceModal
          isOpen={showCadenceCreate}
          onClose={() => setShowCadenceCreate(false)}
          boardId={boardId}
          onSaved={() => { setShowCadenceCreate(false); void loadCadences(); void refreshBoard(); }}
        />
      )}

      {/* Cadence edit modal (Feature 10) */}
      {editCadenceTarget && boardId && (
        <KanbanCadenceModal
          isOpen={!!editCadenceTarget}
          onClose={() => setEditCadenceTarget(null)}
          boardId={boardId}
          cadence={editCadenceTarget}
          onSaved={() => { setEditCadenceTarget(null); void loadCadences(); void refreshBoard(); }}
        />
      )}

      {showSettings && boardData?.board && boardId && (
        <KanbanBoardSettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          boardId={boardId}
          settings={boardSettingsRows}
          onUpdated={() => void refreshBoard()}
        />
      )}

      {showMetrics && boardData?.board && boardId && (
        <KanbanMetricsPanel
          isOpen={showMetrics}
          onClose={() => setShowMetrics(false)}
          boardId={boardId}
          boardTitle={boardData.board.title}
        />
      )}

      {showLinkedEvents && boardId && (
        <BoardLinkedEventsModal boardId={boardId} onClose={() => setShowLinkedEvents(false)} />
      )}

      {showAddLink && (
        <AddCardLinkModal
          isOpen={!!showAddLink}
          onClose={() => setShowAddLink(null)}
          cardId={showAddLink}
          boardId={boardId ?? null}
          onCreated={() => { setShowAddLink(null); void refreshBoard(); }}
        />
      )}

      {/* Edit Swimlane inline modal (Feature 7) */}
      {editSwimlaneTarget && (
        <div className="modal-overlay z-[9999]">
          <div className="modal-panel max-w-sm">
            <button
              type="button"
              onClick={() => setEditSwimlaneTarget(null)}
              className="modal-close"
              aria-label="Close"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="modal-header">
              <h2 className="modal-title">Edit Swimlane</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-sw-title" className="text-xs font-medium text-glass-400 mb-1.5 block">Title</label>
                <input
                  id="edit-sw-title"
                  className="glass-input w-full"
                  value={editSwimlaneTitleVal}
                  onChange={(e) => setEditSwimlaneTitleVal(e.target.value)}
                  disabled={editSwimlaneSubmitting}
                />
              </div>
              <div>
                <label htmlFor="edit-sw-color" className="text-xs font-medium text-glass-400 mb-1.5 block">Color</label>
                <input
                  id="edit-sw-color"
                  type="color"
                  className="h-10 w-14 cursor-pointer rounded-lg border border-glass-200/20 bg-transparent p-0.5"
                  value={editSwimlaneColorVal || '#6366f1'}
                  onChange={(e) => setEditSwimlaneColorVal(e.target.value)}
                  disabled={editSwimlaneSubmitting}
                />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setEditSwimlaneTarget(null)} className="glass-button-secondary" disabled={editSwimlaneSubmitting}>Cancel</button>
                <button
                  type="button"
                  onClick={() => void submitEditSwimlane()}
                  className="btn-primary"
                  disabled={editSwimlaneSubmitting}
                >
                  {editSwimlaneSubmitting && <Loader className="w-4 h-4 animate-spin" />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Modals ────────────────────────────────────────────── */}

      <ConfirmationModal
        isOpen={showDeleteBoard}
        onClose={() => setShowDeleteBoard(false)}
        onConfirm={async () => {
          if (!boardId) return;
          await kanbanApiService.deleteBoard(boardId);
          navigate('/workspaces');
        }}
        title="Delete Board"
        message="This will permanently delete the board and all its lists, cards, labels, and data. This action cannot be undone."
        confirmText="Delete Board"
      />

      <ConfirmationModal
        isOpen={showArchiveBoard}
        onClose={() => setShowArchiveBoard(false)}
        onConfirm={() => void handleArchiveBoard()}
        title="Archive Board"
        message="This board will be archived. You can unarchive it later from the board list."
        confirmText="Archive"
        isLoading={archiveBoardLoading}
      />

      <ConfirmationModal
        isOpen={!!deleteListTarget}
        onClose={() => setDeleteListTarget(null)}
        onConfirm={() => void confirmDeleteList()}
        title="Delete List"
        message="This will permanently delete the list and unassign all cards from it. This cannot be undone."
        confirmText="Delete List"
        isLoading={deleteListLoading}
      />

      <ConfirmationModal
        isOpen={!!deleteSwimlaneTarget}
        onClose={() => setDeleteSwimlaneTarget(null)}
        onConfirm={() => void confirmDeleteSwimlane()}
        title="Delete Swimlane"
        message="This will permanently delete the swimlane. Cards in it will become unsorted."
        confirmText="Delete Swimlane"
        isLoading={deleteSwimlaneLoading}
      />

      <ConfirmationModal
        isOpen={!!deleteCadenceTarget}
        onClose={() => setDeleteCadenceTarget(null)}
        onConfirm={() => void confirmDeleteCadence()}
        title="Delete Cadence"
        message="This will permanently delete this cadence."
        confirmText="Delete"
        isLoading={deleteCadenceLoading}
      />

      <ConfirmationModal
        isOpen={!!deleteCardTarget}
        onClose={() => setDeleteCardTarget(null)}
        onConfirm={() => void confirmDeleteCard()}
        title="Delete Card"
        message={
          deleteCardTarget
            ? `Permanently delete “${deleteCardTarget.title}”? Comments, checklists, and attachments on this card will be removed.`
            : ''
        }
        confirmText="Delete Card"
        isLoading={deleteCardLoading}
      />

      {/* Keyboard Shortcuts Help (Shift+?) */}
      {showShortcutsHelp && (
        <div className="modal-overlay z-[9999]" onClick={() => setShowShortcutsHelp(false)}>
          <div className="modal-panel max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Keyboard Shortcuts</h3>
              <button type="button" onClick={() => setShowShortcutsHelp(false)} className="modal-close" aria-label="Close" title="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="divide-y divide-glass-200/10 text-sm">
              {[
                ['N', 'New card'],
                ['F', 'Toggle filters'],
                ['R', 'Refresh board'],
                ['L', 'Toggle columns / swimlanes (when board has swimlanes)'],
                ['Esc', 'Close panel / clear'],
                ['Shift + ?', 'Show this help'],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-glass-300">{desc}</span>
                  <kbd className="kbd">{key}</kbd>
                </div>
              ))}
            </div>
            <p className="text-xs text-glass-500 mt-4">Shortcuts are disabled while typing in input fields.</p>
          </div>
        </div>
      )}
    </div>
  );
}
